import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CadenceBioApp } from "../../products/medical/web/CadenceBioApp";
import { controlledCommandInput } from "../../products/medical/web/composerSyntax";
import {
  rendererDocumentFromRaw as kernelRendererDocumentFromRaw,
  scientificUiCatalogFromRaw,
} from "../../products/medical/web/ScientificUiKernel";
import {
  agentCountFromLocation,
  demoAdapter,
  publishedArtifactContentFromDirectFile,
  publishedArtifactContentFromRaw,
  projectAdapterFromSnapshot,
  toCadenceArtifact,
} from "../../products/medical/web/adapter";
import { scientificUiArtifactInquirySelection } from "../../products/medical/web/types";
import {
  buildCadenceCollaborationModel,
  collaborationStateFromOrganization,
  mergeOrganizationAgentEvents,
  organizationAgentEventsFromDurable,
  organizationAgentEventFromSse,
  safeCollaborationPreview,
  type OrganizationAgentEventRecord,
} from "../../products/medical/web/collaborationModel";
import type {
  CadenceAttachment,
  CadenceAgent,
  CadenceBTWResponse,
  CadenceComposerCapability,
  CadenceComposerIntent,
  CadenceComposerReference,
  CadenceContext,
  CadenceArtifact,
  CadenceArtifactDetailState,
  CadencePublishedArtifactFormat,
  CadencePublishedArtifactPresentation,
  CadenceEvidence,
  CadencePermissionRequest,
  CadenceTurn,
  CadenceArtifactOpenRequest,
  ArtifactRendererDocument,
  ScientificUiArtifactDocument,
  ScientificArtifactDocument,
  ScientificArtifactInspectionDocument,
  LocalScientificArtifactDocument,
  ScientificOperationResult,
  ReportTextReplacementProposal,
  ScientificSelection,
} from "../../products/medical/web/types";
import {
  api,
  ApiError,
  UploadFilesError,
  type AsyncState,
  type AttachmentRef,
  type ArtifactRecord,
  type BootstrapData,
  type ComposerIntentRequest,
  type ComposerReferenceRequest,
  type EvidenceRecord,
  type MeResponse,
  type OrganizationObservation,
  type OrganizationStatus,
  type PresentationCompletedPayload,
  type PermissionRequest,
  type ProjectTurn,
  type ProjectSummary,
  type ResearchStateSection,
  type ResearchStateView,
  type SessionReplayGap,
  type SessionSnapshot,
  type SseEvent,
} from "./api";
import { useSessionStream } from "./hooks/useSessionStream";

const EMPTY_CADENCE_AGENTS: CadenceAgent[] = [];
const INITIAL_RESEARCH_STATE_SECTIONS: ResearchStateSection[] = [
  "entry",
  "current",
  "conflicts",
  "negatives",
];
const ORGANIZATION_READ_RETRY_LIMIT = 2;
type CadenceOnboardingStatus = "completed" | "skipped";

interface ArtifactInquirySubmit {
  artifact: CadenceArtifact;
  revision?: number;
  selection?: ScientificSelection;
  intent: "query" | "change_proposal";
  input: string;
}

/**
 * A rendered project slice is UI data only. The API/SSE remain authoritative;
 * this cache lets a researcher return to a project without briefly seeing
 * another project's snapshot, Artifact details, or request timeline.
 */
interface ProjectViewCacheEntry {
  projectContentState: AsyncState;
  projectSnapshotError?: string;
  snapshot?: SessionSnapshot;
  listedArtifacts: ArtifactRecord[];
  artifactDetails: Record<string, CadenceArtifact>;
  artifactDetailStates: Record<string, CadenceArtifactDetailState>;
  artifactDetailErrors: Record<string, string>;
  organization?: OrganizationObservation;
  organizationError?: string;
  organizationStatuses: Record<string, OrganizationStatus>;
  organizationAgentEvents: OrganizationAgentEventRecord[];
  projectTurns: ProjectTurn[];
  optimisticTurn?: ProjectTurn;
  turnsNextCursor: string | null;
  turnsHasMore: boolean;
  turnsError?: string;
  turnsErrorOrigin?: "head" | "older";
  turnsErrorCursor?: string;
  scientificOperation?: ScientificOperationResult;
  researchStateViews: Partial<Record<ResearchStateSection, ResearchStateView>>;
  researchStateLoadingSections: ResearchStateSection[];
  researchStateErrors: Partial<Record<ResearchStateSection, string>>;
  loadedResearchStateSections: ResearchStateSection[];
  composerCapabilities: CadenceComposerCapability[];
  availableAttachments: AttachmentRef[];
  attachments: AttachmentRef[];
}

function emptyProjectView(): ProjectViewCacheEntry {
  return {
    projectContentState: "loading",
    listedArtifacts: [],
    artifactDetails: {},
    artifactDetailStates: {},
    artifactDetailErrors: {},
    organizationStatuses: {},
    organizationAgentEvents: [],
    projectTurns: [],
    turnsNextCursor: null,
    turnsHasMore: false,
    researchStateViews: {},
    researchStateLoadingSections: [],
    researchStateErrors: {},
    loadedResearchStateSections: [],
    composerCapabilities: [],
    availableAttachments: [],
    attachments: [],
  };
}

function cadenceOnboardingStorageKey(userId: string): string {
  return `cadence.onboarding.cadence-bio.v1.${userId}`;
}

function readCadenceOnboardingStatus(
  userId: string,
): CadenceOnboardingStatus | undefined {
  try {
    const value = window.localStorage.getItem(
      cadenceOnboardingStorageKey(userId),
    );
    if (!value) return undefined;
    const parsed = JSON.parse(value) as { status?: unknown };
    return parsed.status === "completed" || parsed.status === "skipped"
      ? parsed.status
      : undefined;
  } catch {
    return undefined;
  }
}

function writeCadenceOnboardingStatus(
  userId: string,
  status: CadenceOnboardingStatus,
) {
  try {
    window.localStorage.setItem(
      cadenceOnboardingStorageKey(userId),
      JSON.stringify({ status }),
    );
  } catch {
    // Persistence is best effort.
  }
}

export function CadenceSurface() {
  const agentCount = agentCountFromLocation();
  const query = new URLSearchParams(
    typeof window === "undefined" ? "" : window.location.search,
  );
  const demoRequested =
    import.meta.env.DEV &&
    typeof window !== "undefined" &&
    (query.has("agents") || query.get("demo") === "1");
  const demo = useMemo(() => demoAdapter(agentCount), [agentCount]);
  const [bootstrap, setBootstrap] = useState<BootstrapData>();
  const [bootstrapError, setBootstrapError] = useState<string>();
  const [identity, setIdentity] = useState<MeResponse>();
  const [identityError, setIdentityError] = useState<string>();
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [projectListState, setProjectListState] = useState<AsyncState>(
    demoRequested ? "ready" : "loading",
  );
  const [projectListError, setProjectListError] = useState<string>();
  const [activeProjectId, setActiveProjectId] = useState<string>();
  const [projectContentState, setProjectContentState] =
    useState<AsyncState>("idle");
  const [projectSnapshotError, setProjectSnapshotError] = useState<string>();
  const [snapshot, setSnapshot] = useState<SessionSnapshot>();
  const [listedArtifacts, setListedArtifacts] = useState<ArtifactRecord[]>([]);
  const listedArtifactsRef = useRef<ArtifactRecord[]>([]);
  const [composerCapabilities, setComposerCapabilities] = useState<
    CadenceComposerCapability[]
  >([]);
  const [availableAttachments, setAvailableAttachments] = useState<
    AttachmentRef[]
  >([]);
  const [attachments, setAttachments] = useState<AttachmentRef[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>();
  const [btwResponse, setBtwResponse] = useState<CadenceBTWResponse>();
  const [artifactDetails, setArtifactDetails] = useState<
    Record<string, CadenceArtifact>
  >({});
  const [artifactDetailStates, setArtifactDetailStates] = useState<
    Record<string, CadenceArtifactDetailState>
  >({});
  const [artifactDetailErrors, setArtifactDetailErrors] = useState<
    Record<string, string>
  >({});
  const [artifactOpenRequest, setArtifactOpenRequest] =
    useState<CadenceArtifactOpenRequest>();
  const [organization, setOrganization] = useState<OrganizationObservation>();
  const [organizationError, setOrganizationError] = useState<string>();
  const [organizationStatuses, setOrganizationStatuses] = useState<
    Record<string, OrganizationStatus>
  >({});
  const [organizationAgentEvents, setOrganizationAgentEvents] = useState<
    OrganizationAgentEventRecord[]
  >([]);
  const [projectTurns, setProjectTurns] = useState<ProjectTurn[]>([]);
  const [optimisticTurn, setOptimisticTurn] = useState<ProjectTurn>();
  const [turnsNextCursor, setTurnsNextCursor] = useState<string | null>(null);
  const [turnsHasMore, setTurnsHasMore] = useState(false);
  const [turnsLoading, setTurnsLoading] = useState(false);
  const [turnsLoadingOlder, setTurnsLoadingOlder] = useState(false);
  const [turnsError, setTurnsError] = useState<string>();
  const [turnsErrorOrigin, setTurnsErrorOrigin] = useState<"head" | "older">();
  const [turnsErrorCursor, setTurnsErrorCursor] = useState<string>();
  const [creatingProject, setCreatingProject] = useState(false);
  const [createProjectError, setCreateProjectError] = useState<string>();
  const [projectNotice, setProjectNotice] = useState<string>();
  const [requestError, setRequestError] = useState<string>();
  const [permissionError, setPermissionError] = useState<string>();
  const [onboardingActive, setOnboardingActive] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const onboardingUserRef = useRef<string>();
  const onboardingStartedRef = useRef(false);
  const onboardingWaitingForProjectRef = useRef(false);
  const [scientificOperation, setScientificOperation] =
    useState<ScientificOperationResult>();
  const [researchStateViews, setResearchStateViews] = useState<
    Partial<Record<ResearchStateSection, ResearchStateView>>
  >({});
  const [researchStateLoadingSections, setResearchStateLoadingSections] =
    useState<ResearchStateSection[]>([]);
  const [researchStateErrors, setResearchStateErrors] = useState<
    Partial<Record<ResearchStateSection, string>>
  >({});
  const scopeGenerationRef = useRef(0);
  const contentRequestRef = useRef(0);
  const turnsHeadRequestRef = useRef(0);
  const turnsOlderRequestRef = useRef(0);
  const turnsOlderInFlightRef = useRef(false);
  const turnsPaginationAdvancedRef = useRef(false);
  const activeProjectIdRef = useRef<string>();
  const projectsRef = useRef<ProjectSummary[]>([]);
  const projectListRequestRef = useRef(0);
  const seededSnapshotsRef = useRef(new Map<string, SessionSnapshot>());
  const failedUploadFilesRef = useRef<File[]>([]);
  const artifactOpenRequestIdRef = useRef(0);
  const permissionPendingRef = useRef(new Set<string>());
  const cancelPendingRef = useRef(new Set<string>());
  const artifactDetailPendingRef = useRef(new Set<string>());
  const artifactDetailGenerationRef = useRef(new Map<string, number>());
  const organizationRequestRef = useRef(0);
  const organizationRetryTimerRef = useRef<number>();
  const contentRetryTimerRef = useRef<number>();
  const activationObservationAttemptsRef = useRef(new Map<string, number>());
  const loadedResearchStateSectionsRef = useRef(
    new Set<ResearchStateSection>(),
  );
  const researchStatePendingRef = useRef(new Set<ResearchStateSection>());
  const projectViewCacheRef = useRef(
    new Map<string, ProjectViewCacheEntry>(),
  );
  const projectViewStateRef = useRef<ProjectViewCacheEntry>();
  const cancelContentRetry = useCallback(() => {
    if (contentRetryTimerRef.current === undefined) return;
    window.clearTimeout(contentRetryTimerRef.current);
    contentRetryTimerRef.current = undefined;
  }, []);

  const replaceProjects = useCallback((nextProjects: ProjectSummary[]) => {
    projectsRef.current = nextProjects;
    setProjects(nextProjects);
  }, []);

  useEffect(() => {
    listedArtifactsRef.current = listedArtifacts;
  }, [listedArtifacts]);

  projectViewStateRef.current = {
    projectContentState,
    projectSnapshotError,
    snapshot,
    listedArtifacts,
    artifactDetails,
    artifactDetailStates,
    artifactDetailErrors,
    organization,
    organizationError,
    organizationStatuses,
    organizationAgentEvents,
    projectTurns,
    optimisticTurn,
    turnsNextCursor,
    turnsHasMore,
    turnsError,
    turnsErrorOrigin,
    turnsErrorCursor,
    scientificOperation,
    researchStateViews,
    researchStateLoadingSections,
    researchStateErrors,
    loadedResearchStateSections: [...loadedResearchStateSectionsRef.current],
    composerCapabilities,
    availableAttachments,
    attachments,
  };

  const snapshotProjectView = useCallback((projectId?: string) => {
    if (!projectId) return;
    const current = projectViewStateRef.current;
    if (current) projectViewCacheRef.current.set(projectId, current);
  }, []);

  const restoreProjectView = useCallback((projectId: string) => {
    const cached = projectViewCacheRef.current.get(projectId);
    const view = cached ?? emptyProjectView();
    projectViewStateRef.current = view;
    listedArtifactsRef.current = view.listedArtifacts;
    setProjectContentState(view.projectContentState);
    setProjectSnapshotError(view.projectSnapshotError);
    setSnapshot(view.snapshot);
    setListedArtifacts(view.listedArtifacts);
    setArtifactDetails(view.artifactDetails);
    setArtifactDetailStates(view.artifactDetailStates);
    setArtifactDetailErrors(view.artifactDetailErrors);
    setOrganization(view.organization);
    setOrganizationError(view.organizationError);
    setOrganizationStatuses(view.organizationStatuses);
    setOrganizationAgentEvents(view.organizationAgentEvents);
    setProjectTurns(view.projectTurns);
    setOptimisticTurn(view.optimisticTurn);
    setTurnsNextCursor(view.turnsNextCursor);
    setTurnsHasMore(view.turnsHasMore);
    setTurnsLoading(false);
    setTurnsLoadingOlder(false);
    setTurnsError(view.turnsError);
    setTurnsErrorOrigin(view.turnsErrorOrigin);
    setTurnsErrorCursor(view.turnsErrorCursor);
    setScientificOperation(view.scientificOperation);
    setResearchStateViews(view.researchStateViews);
    setResearchStateLoadingSections(view.researchStateLoadingSections);
    setResearchStateErrors(view.researchStateErrors);
    setComposerCapabilities(view.composerCapabilities);
    setAvailableAttachments(view.availableAttachments);
    setAttachments(view.attachments);
    loadedResearchStateSectionsRef.current = new Set(
      view.loadedResearchStateSections,
    );
    researchStatePendingRef.current.clear();
    return Boolean(cached);
  }, []);

  const activeProject = projects.find(
    (project) => project.projectId === activeProjectId,
  );
  const sessionId = activeProject?.sessionId;

  useEffect(() => {
    document.body.classList.add("cadence-page");
    return () => {
      document.body.classList.remove("cadence-page");
      cancelContentRetry();
    };
  }, [cancelContentRetry]);

  const clearProjectScope = useCallback(() => {
    scopeGenerationRef.current += 1;
    contentRequestRef.current += 1;
    turnsHeadRequestRef.current += 1;
    turnsOlderRequestRef.current += 1;
    organizationRequestRef.current += 1;
    turnsOlderInFlightRef.current = false;
    turnsPaginationAdvancedRef.current = false;
    if (organizationRetryTimerRef.current !== undefined) {
      window.clearTimeout(organizationRetryTimerRef.current);
      organizationRetryTimerRef.current = undefined;
    }
    cancelContentRetry();
    listedArtifactsRef.current = [];
    setSnapshot(undefined);
    setListedArtifacts([]);
    setComposerCapabilities([]);
    setAvailableAttachments([]);
    setAttachments([]);
    setUploading(false);
    setUploadError(undefined);
    failedUploadFilesRef.current = [];
    permissionPendingRef.current.clear();
    cancelPendingRef.current.clear();
    artifactDetailPendingRef.current.clear();
    artifactDetailGenerationRef.current.clear();
    setBtwResponse(undefined);
    setArtifactDetails({});
    setArtifactDetailStates({});
    setArtifactDetailErrors({});
    setArtifactOpenRequest(undefined);
    setRequestError(undefined);
    setPermissionError(undefined);
    setScientificOperation(undefined);
    setResearchStateViews({});
    setResearchStateLoadingSections([]);
    setResearchStateErrors({});
    loadedResearchStateSectionsRef.current.clear();
    researchStatePendingRef.current.clear();
    setProjectSnapshotError(undefined);
    setProjectContentState("idle");
    setOrganization(undefined);
    setOrganizationError(undefined);
    setOrganizationStatuses({});
    setOrganizationAgentEvents([]);
    setProjectTurns([]);
    setOptimisticTurn(undefined);
    setTurnsNextCursor(null);
    setTurnsHasMore(false);
    setTurnsLoading(false);
    setTurnsLoadingOlder(false);
    setTurnsError(undefined);
    setTurnsErrorOrigin(undefined);
    setTurnsErrorCursor(undefined);
  }, [cancelContentRetry]);

  const selectProject = useCallback(
    (projectId: string) => {
      if (projectId === activeProjectIdRef.current) return;
      snapshotProjectView(activeProjectIdRef.current);
      clearProjectScope();
      activeProjectIdRef.current = projectId;
      restoreProjectView(projectId);
      setActiveProjectId(projectId);
      try {
        window.localStorage.setItem("cadence.project", projectId);
      } catch {
        // Persistence is best effort.
      }
    },
    [clearProjectScope, restoreProjectView, snapshotProjectView],
  );

  const loadProjects = useCallback(async () => {
    if (demoRequested) {
      setProjectListState("ready");
      return;
    }
    const requestId = ++projectListRequestRef.current;
    setProjectListState("loading");
    setProjectListError(undefined);
    const [bootstrapResult, identityResult, projectsResult] =
      await Promise.allSettled([
        api.bootstrap(),
        api.getMe(),
        api.listProjects(),
      ]);

    if (requestId !== projectListRequestRef.current) return;

    if (bootstrapResult.status === "fulfilled") {
      setBootstrap(bootstrapResult.value);
      setBootstrapError(undefined);
    } else {
      setBootstrap(undefined);
      setBootstrapError(
        errorMessage(bootstrapResult.reason, "运行时信息加载失败。"),
      );
    }
    if (identityResult.status === "fulfilled") {
      setIdentity(identityResult.value);
      setIdentityError(undefined);
    } else {
      setIdentity(undefined);
      setIdentityError(
        errorMessage(identityResult.reason, "用户身份加载失败。"),
      );
    }
    if (projectsResult.status === "rejected") {
      setProjectListState("error");
      setProjectListError(
        errorMessage(projectsResult.reason, "项目列表加载失败。"),
      );
      return;
    }

    const nextProjects = projectsResult.value;
    replaceProjects(nextProjects);
    setProjectListState("ready");
    const savedProjectId = (() => {
      try {
        return window.localStorage.getItem("cadence.project");
      } catch {
        return null;
      }
    })();
    const nextActive =
      nextProjects.find(
        (project) => project.projectId === activeProjectIdRef.current,
      ) ??
      nextProjects.find((project) => project.projectId === savedProjectId) ??
      nextProjects[0];
    if (!nextActive) {
      if (activeProjectIdRef.current) clearProjectScope();
      activeProjectIdRef.current = undefined;
      setActiveProjectId(undefined);
      return;
    }
    try {
      window.localStorage.setItem("cadence.project", nextActive.projectId);
    } catch {
      // Persistence is best effort.
    }
    if (nextActive.projectId !== activeProjectIdRef.current) {
      snapshotProjectView(activeProjectIdRef.current);
      clearProjectScope();
      activeProjectIdRef.current = nextActive.projectId;
      restoreProjectView(nextActive.projectId);
      setActiveProjectId(nextActive.projectId);
    }
  }, [
    clearProjectScope,
    demoRequested,
    replaceProjects,
    restoreProjectView,
    snapshotProjectView,
  ]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (
      demoRequested ||
      !identity?.userId ||
      projectListState !== "ready"
    ) {
      return;
    }
    if (onboardingUserRef.current !== identity.userId) {
      onboardingUserRef.current = identity.userId;
      onboardingStartedRef.current = false;
      setOnboardingActive(false);
      setOnboardingStep(0);
    }
    if (onboardingStartedRef.current) return;
    onboardingStartedRef.current = true;
    if (!readCadenceOnboardingStatus(identity.userId)) {
      onboardingWaitingForProjectRef.current = projects.length === 0;
      setOnboardingActive(true);
    }
  }, [demoRequested, identity?.userId, projectListState, projects.length]);

  useEffect(() => {
    if (
      onboardingActive &&
      onboardingStep === 0 &&
      onboardingWaitingForProjectRef.current &&
      projects.length > 0
    ) {
      onboardingWaitingForProjectRef.current = false;
      setOnboardingStep(1);
    }
  }, [onboardingActive, onboardingStep, projects.length]);

  const dismissOnboarding = useCallback(() => {
    if (identity?.userId) {
      writeCadenceOnboardingStatus(identity.userId, "skipped");
    }
    onboardingWaitingForProjectRef.current = false;
    setOnboardingActive(false);
  }, [identity?.userId]);

  const pauseOnboarding = useCallback(() => {
    onboardingWaitingForProjectRef.current = false;
    setOnboardingActive(false);
  }, []);

  const completeOnboarding = useCallback(() => {
    if (identity?.userId) {
      writeCadenceOnboardingStatus(identity.userId, "completed");
    }
    onboardingWaitingForProjectRef.current = false;
    setOnboardingActive(false);
  }, [identity?.userId]);

  const replayOnboarding = useCallback(() => {
    if (demoRequested || !identity?.userId) return;
    onboardingWaitingForProjectRef.current = projects.length === 0;
    setOnboardingStep(0);
    setOnboardingActive(true);
  }, [demoRequested, identity?.userId, projects.length]);

  const loadProjectContent = useCallback(
    async function loadProjectContent(
      project: ProjectSummary,
      seed?: SessionSnapshot,
      showLoading = true,
      retryAttempt = 0,
    ): Promise<ArtifactRecord[] | undefined> {
      cancelContentRetry();
      const scopeGeneration = scopeGenerationRef.current;
      const requestId = ++contentRequestRef.current;
      const requestIsCurrent = () =>
        requestId === contentRequestRef.current &&
        scopeGeneration === scopeGenerationRef.current &&
        project.projectId === activeProjectIdRef.current;
      const scheduleRetry = (retrySeed?: SessionSnapshot) => {
        if (retryAttempt >= 2 || !requestIsCurrent()) return;
        contentRetryTimerRef.current = window.setTimeout(
          () => {
            contentRetryTimerRef.current = undefined;
            if (!requestIsCurrent()) return;
            void loadProjectContent(
              project,
              retrySeed,
              false,
              retryAttempt + 1,
            );
          },
          1500 * (retryAttempt + 1),
        );
      };
      if (showLoading) {
        setProjectContentState("loading");
        setProjectSnapshotError(undefined);
      }
      const [snapshotResult, artifactsResult, uploadsResult, capabilitiesResult] = await Promise.allSettled([
        seed ? Promise.resolve(seed) : api.getSnapshot(project.sessionId),
        api.listArtifacts(project.sessionId),
        api.listUploads(project.sessionId),
        api.getComposerCapabilities(project.projectId),
      ]);
      if (!requestIsCurrent()) return undefined;

      const nextSnapshot =
        snapshotResult.status === "fulfilled" ? snapshotResult.value : undefined;
      const snapshotArtifacts = nextSnapshot?.artifacts ?? [];
      const nextArtifacts =
        artifactsResult.status === "fulfilled"
          ? artifactsResult.value
          : snapshotArtifacts.length > 0
            ? snapshotArtifacts
            : listedArtifactsRef.current;

      // The Artifact directory is an independently durable research record. A
      // Runtime restore failure leaves live execution state unknown, but must not
      // erase or hide a directory that was read successfully from that record.
      if (artifactsResult.status === "fulfilled" || nextSnapshot) {
        listedArtifactsRef.current = nextArtifacts;
        setListedArtifacts(nextArtifacts);
      }
      if (nextSnapshot) {
        setSnapshot({ ...nextSnapshot, artifacts: nextArtifacts });
        const durableEvents = organizationAgentEventsFromDurable(
          nextSnapshot.raw,
          project.projectId,
          project.sessionId,
        );
        if (durableEvents.length) {
          setOrganizationAgentEvents((current) =>
            mergeOrganizationAgentEvents(current, durableEvents),
          );
        }
        const nextScientificOperation =
          scientificOperationFromUnknown(nextSnapshot);
        if (nextScientificOperation) {
          setScientificOperation(nextScientificOperation);
        }
      }
      if (uploadsResult.status === "fulfilled") {
        // Project Inputs are durable; the Composer selection is not. Keeping
        // those collections distinct lets `@` add a known upload without
        // silently attaching every historical Project file to a new request.
        setAvailableAttachments(uploadsResult.value);
        setAttachments((current) =>
          refreshSelectedAttachments(current, uploadsResult.value),
        );
      }
      if (capabilitiesResult.status === "fulfilled") {
        setComposerCapabilities(capabilitiesResult.value);
      }
      if (artifactsResult.status === "rejected") {
        setProjectContentState("error");
      } else {
        setProjectContentState("ready");
      }
      setProjectSnapshotError(
        snapshotResult.status === "rejected"
          ? "项目运行快照暂时无法恢复；产物目录仍可继续查看。"
          : undefined,
      );
      if (
        snapshotResult.status === "rejected" ||
        artifactsResult.status === "rejected" ||
        uploadsResult.status === "rejected"
        || capabilitiesResult.status === "rejected"
      ) {
        scheduleRetry(nextSnapshot);
      }
      return artifactsResult.status === "fulfilled" ? nextArtifacts : undefined;
    },
    [cancelContentRetry],
  );

  const refreshProjectTurns = useCallback(
    async (projectId: string, showLoading = false) => {
      if (!api.listProjectTurns || projectId !== activeProjectIdRef.current)
        return;
      const requestId = ++turnsHeadRequestRef.current;
      if (showLoading) {
        setTurnsLoading(true);
      }
      setTurnsError(undefined);
      setTurnsErrorOrigin(undefined);
      setTurnsErrorCursor(undefined);
      try {
        const page = await api.listProjectTurns(projectId);
        if (
          requestId !== turnsHeadRequestRef.current ||
          projectId !== activeProjectIdRef.current
        ) {
          return;
        }
        setProjectTurns((current) =>
          showLoading && current.length === 0
            ? page.turns
            : mergeRefreshedProjectTurns(current, page.turns),
        );
        if (!turnsPaginationAdvancedRef.current) {
          setTurnsNextCursor(page.nextCursor);
          setTurnsHasMore(page.hasMore);
        }
        setOptimisticTurn((current) =>
          current &&
          page.turns.some((turn) => turn.requestId === current.requestId)
            ? undefined
            : current,
        );
      } catch (cause) {
        if (
          requestId === turnsHeadRequestRef.current &&
          projectId === activeProjectIdRef.current
        ) {
          setTurnsError(errorMessage(cause, "项目请求历史加载失败。"));
          setTurnsErrorOrigin("head");
        }
      } finally {
        if (
          requestId === turnsHeadRequestRef.current &&
          projectId === activeProjectIdRef.current
        ) {
          setTurnsLoading(false);
        }
      }
    },
    [],
  );

  const loadOlderProjectTurns = useCallback(async (cursorOverride?: string) => {
    const projectId = activeProjectIdRef.current;
    const cursor = cursorOverride ?? turnsNextCursor;
    if (
      !api.listProjectTurns ||
      !projectId ||
      !cursor ||
      turnsLoadingOlder ||
      turnsOlderInFlightRef.current
    ) {
      return;
    }
    const requestId = ++turnsOlderRequestRef.current;
    turnsOlderInFlightRef.current = true;
    setTurnsLoadingOlder(true);
    setTurnsError(undefined);
    setTurnsErrorOrigin(undefined);
    setTurnsErrorCursor(undefined);
    try {
      const page = await api.listProjectTurns(projectId, cursor);
      if (
        requestId !== turnsOlderRequestRef.current ||
        projectId !== activeProjectIdRef.current
      ) {
        return;
      }
      setProjectTurns((current) =>
        dedupeProjectTurns([...page.turns, ...current]),
      );
      turnsPaginationAdvancedRef.current = true;
      setTurnsNextCursor(page.nextCursor);
      setTurnsHasMore(page.hasMore);
    } catch (cause) {
      if (
        requestId === turnsOlderRequestRef.current &&
        projectId === activeProjectIdRef.current
      ) {
        setTurnsError(errorMessage(cause, "更早请求加载失败。"));
        setTurnsErrorOrigin("older");
        setTurnsErrorCursor(cursor);
      }
    } finally {
      if (
        requestId === turnsOlderRequestRef.current &&
        projectId === activeProjectIdRef.current
      ) {
        turnsOlderInFlightRef.current = false;
        setTurnsLoadingOlder(false);
      }
    }
  }, [turnsLoadingOlder, turnsNextCursor]);

  const refreshSnapshot = useCallback(
    async (
      projectId: string,
      sessionId: string,
      scopeGeneration: number,
    ): Promise<boolean> => {
      if (
        scopeGeneration !== scopeGenerationRef.current ||
        projectId !== activeProjectIdRef.current
      ) {
        return false;
      }
      cancelContentRetry();
      const requestId = ++contentRequestRef.current;
      try {
        const nextSnapshot = await api.getSnapshot(sessionId);
        if (
          requestId !== contentRequestRef.current ||
          scopeGeneration !== scopeGenerationRef.current ||
          projectId !== activeProjectIdRef.current
        ) {
          return false;
        }
        listedArtifactsRef.current = nextSnapshot.artifacts;
        setListedArtifacts(nextSnapshot.artifacts);
        setSnapshot(nextSnapshot);
        const durableEvents = organizationAgentEventsFromDurable(
          nextSnapshot.raw,
          projectId,
          sessionId,
        );
        if (durableEvents.length) {
          setOrganizationAgentEvents((current) =>
            mergeOrganizationAgentEvents(current, durableEvents),
          );
        }
        setScientificOperation(scientificOperationFromUnknown(nextSnapshot));
        setProjectContentState("ready");
        setProjectSnapshotError(undefined);
        return true;
      } catch (cause) {
        if (
          requestId === contentRequestRef.current &&
          scopeGeneration === scopeGenerationRef.current &&
          projectId === activeProjectIdRef.current
        ) {
          setProjectSnapshotError(
            "项目运行快照暂时无法刷新；产物目录仍可继续查看。",
          );
        }
        throw cause;
      }
    },
    [cancelContentRetry],
  );

  useEffect(() => {
    if (demoRequested) return;
    if (!activeProject) {
      setProjectContentState("idle");
      return;
    }
    const seed = seededSnapshotsRef.current.get(activeProject.projectId);
    seededSnapshotsRef.current.delete(activeProject.projectId);
    const hasCachedView = projectViewCacheRef.current.has(activeProject.projectId);
    // Keep a cached project visible while its authoritative snapshot revalidates.
    // The request/scope guards below still prevent late responses from crossing projects.
    void loadProjectContent(activeProject, seed, !hasCachedView);
  }, [activeProject?.projectId, demoRequested, loadProjectContent]);

  useEffect(() => {
    if (demoRequested || !activeProject) return;
    void refreshProjectTurns(activeProject.projectId, true);
  }, [activeProject?.projectId, demoRequested, refreshProjectTurns]);

  const refreshOrganization = useCallback(
    async function refreshOrganization(
      projectId: string,
      retryAttempt = 0,
    ): Promise<OrganizationObservation | undefined> {
      const scheduleRetry = () => {
        if (retryAttempt >= ORGANIZATION_READ_RETRY_LIMIT) return;
        // A team read is a non-critical observation, not a background worker.
        // Bound and desynchronise retry attempts so a dependency outage cannot
        // turn every open Project into a permanent polling load on the server.
        const retryDelay =
          Math.min(3_000, 600 * 2 ** retryAttempt) +
          Math.floor(Math.random() * 300);
        organizationRetryTimerRef.current = window.setTimeout(
          () => {
            organizationRetryTimerRef.current = undefined;
            void refreshOrganization(projectId, retryAttempt + 1);
          },
          retryDelay,
        );
      };
      if (demoRequested || projectId !== activeProjectIdRef.current) {
        return undefined;
      }
      if (
        retryAttempt === 0 &&
        organizationRetryTimerRef.current !== undefined
      ) {
        window.clearTimeout(organizationRetryTimerRef.current);
        organizationRetryTimerRef.current = undefined;
      }
      const scopeGeneration = scopeGenerationRef.current;
      const requestId = ++organizationRequestRef.current;
      const requestIsCurrent = () =>
        requestId === organizationRequestRef.current &&
        scopeGeneration === scopeGenerationRef.current &&
        projectId === activeProjectIdRef.current;
      try {
        const nextOrganization =
          await api.getOrganizationObservation(projectId);
        if (!requestIsCurrent()) return;
        setOrganization(nextOrganization);
        const durableEvents = organizationAgentEventsFromDurable(
          nextOrganization.raw,
          projectId,
          "",
        );
        if (durableEvents.length) {
          setOrganizationAgentEvents((current) =>
            mergeOrganizationAgentEvents(current, durableEvents),
          );
        }
        // An activation error belongs to the ProjectTurn that requested the
        // work, not to the configured roster. Showing it as a team-read error
        // hid usable Agents and Artifacts after a single research request
        // failed; the timeline remains the authoritative place for that turn.
        setOrganizationError(undefined);
        if (organizationRetryTimerRef.current !== undefined) {
          window.clearTimeout(organizationRetryTimerRef.current);
          organizationRetryTimerRef.current = undefined;
        }
        return nextOrganization;
      } catch (cause) {
        if (!requestIsCurrent()) return;
        setOrganizationError("研究团队暂时无法刷新。请稍后重试。");
        const retryable =
          !(cause instanceof ApiError) ||
          cause.status === 408 ||
          cause.status === 429 ||
          cause.status >= 500;
        if (retryable) scheduleRetry();
        return undefined;
      }
    },
    [demoRequested],
  );

  const loadResearchStateSection = useCallback(
    async (projectId: string, section: ResearchStateSection) => {
      if (
        demoRequested ||
        projectId !== activeProjectIdRef.current ||
        researchStatePendingRef.current.has(section)
      ) {
        return;
      }
      const scopeGeneration = scopeGenerationRef.current;
      researchStatePendingRef.current.add(section);
      setResearchStateLoadingSections((current) =>
        current.includes(section) ? current : [...current, section],
      );
      setResearchStateErrors((current) => {
        if (!(section in current)) return current;
        const next = { ...current };
        delete next[section];
        return next;
      });
      try {
        const view = await api.getProjectResearchState(projectId, section, {
          limit: 50,
        });
        if (
          scopeGeneration !== scopeGenerationRef.current ||
          projectId !== activeProjectIdRef.current
        ) {
          return;
        }
        loadedResearchStateSectionsRef.current.add(section);
        setResearchStateViews((current) => ({ ...current, [section]: view }));
      } catch (cause) {
        if (
          scopeGeneration === scopeGenerationRef.current &&
          projectId === activeProjectIdRef.current
        ) {
          setResearchStateErrors((current) => ({
            ...current,
            [section]: errorMessage(
              cause,
              `研究记录 ${section} 分节加载失败。`,
            ),
          }));
        }
      } finally {
        researchStatePendingRef.current.delete(section);
        if (
          scopeGeneration === scopeGenerationRef.current &&
          projectId === activeProjectIdRef.current
        ) {
          setResearchStateLoadingSections((current) =>
            current.filter((item) => item !== section),
          );
        }
      }
    },
    [demoRequested],
  );

  const loadResearchStateSections = useCallback(
    (projectId: string, sections: ResearchStateSection[]) => {
      sections.forEach((section) => {
        void loadResearchStateSection(projectId, section);
      });
    },
    [loadResearchStateSection],
  );

  const refreshLoadedResearchState = useCallback(
    (projectId: string) => {
      const sections = [...loadedResearchStateSectionsRef.current];
      if (sections.length) loadResearchStateSections(projectId, sections);
    },
    [loadResearchStateSections],
  );

  useEffect(() => {
    if (demoRequested || !activeProject) {
      setOrganization(undefined);
      setOrganizationError(undefined);
      setOrganizationStatuses({});
      return;
    }
    void refreshOrganization(activeProject.projectId);
  }, [activeProject?.projectId, demoRequested, refreshOrganization]);

  useEffect(() => {
    if (demoRequested || !activeProject || !organizationError) return undefined;
    const retry = () => {
      void refreshOrganization(activeProject.projectId);
    };
    window.addEventListener("focus", retry);
    window.addEventListener("online", retry);
    return () => {
      window.removeEventListener("focus", retry);
      window.removeEventListener("online", retry);
    };
  }, [
    activeProject?.projectId,
    demoRequested,
    organizationError,
    refreshOrganization,
  ]);

  useEffect(() => {
    const projectId = activeProject?.projectId;
    if (!projectId || !organization?.enabled || !organization.agents.length) {
      setOrganizationStatuses({});
      return undefined;
    }
    const scopeGeneration = scopeGenerationRef.current;
    let cancelled = false;
    const liveAgents = organization.agents.filter((agent) => agent.liveWorker);
    Promise.allSettled(
      liveAgents.map(async (agent) => ({
        agentId: agent.id,
        status: await api.getOrganizationAgentStatus(projectId, agent.id),
      })),
    ).then((results) => {
      if (
        cancelled ||
        scopeGeneration !== scopeGenerationRef.current ||
        projectId !== activeProjectIdRef.current
      ) {
        return;
      }
      // A single worker's status is supplementary telemetry. It must not be
      // interpreted as proof that the configured team vanished, nor erase the
      // last useful status while the rest of the roster remains observable.
      setOrganizationStatuses((current) => {
        const next: Record<string, OrganizationStatus> = {};
        results.forEach((result, index) => {
          const agent = liveAgents[index];
          if (!agent) return;
          if (result.status === "fulfilled") {
            next[agent.id] = result.value.status;
            return;
          }
          const previous = current[agent.id];
          next[agent.id] = {
            version: previous?.version ?? 1,
            agentId: agent.id,
            status: previous?.status ?? "unknown",
            recentSummaries: previous?.recentSummaries ?? [],
            statusError: "该 Agent 的运行状态暂时无法读取。",
            raw: previous?.raw ?? {},
          };
        });
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [activeProject?.projectId, organization]);

  const handleEvent = useCallback(
    async (event: SseEvent) => {
      const type = event.type.toLowerCase();
      const effectiveType =
        type === "organization.agent_event"
          ? organizationAgentEventType(event.payload)
          : type;
      if (
        activeProject &&
        shouldRefreshProjectTurnsForEvent(effectiveType)
      ) {
        void refreshProjectTurns(activeProject.projectId);
      }
      if (
        activeProject &&
        shouldRefreshResearchStateForEvent(effectiveType)
      ) {
        refreshLoadedResearchState(activeProject.projectId);
      }
      if (type === "organization.agent_event") {
        const agentEventType = organizationAgentEventType(event.payload);
        const organizationAgentEvent = organizationAgentEventFromSse(
          event,
          activeProject?.projectId ?? "",
          activeProject?.sessionId ?? "",
        );
        if (organizationAgentEvent) {
          // Project-session SSE is the authoritative ordered fan-in for
          // Organization activity. Per-Agent streams have weaker replay
          // semantics and can exhaust the browser's HTTP/1.1 connections.
          setOrganizationAgentEvents((current) =>
            mergeOrganizationAgentEvents(current, organizationAgentEvent),
          );
        }
        const operation = scientificOperationFromUnknown(event.payload);
        if (operation) {
          setScientificOperation({
            ...operation,
            agent_id:
              typeof event.payload.agent_id === "string"
                ? event.payload.agent_id
                : operation.agent_id,
            thread_id:
              typeof event.payload.thread_id === "string"
                ? event.payload.thread_id
                : operation.thread_id,
          });
        }
        if (
          activeProject &&
          shouldRefreshOrganizationForAgentEvent(agentEventType)
        ) {
          void refreshOrganization(activeProject.projectId);
        }
        if (
          activeProject &&
          shouldRefreshSnapshotForAgentEvent(agentEventType)
        ) {
          void refreshSnapshot(
            activeProject.projectId,
            activeProject.sessionId,
            scopeGenerationRef.current,
          );
        }
      }
      if (
        activeProject &&
        (type.includes("artifact") ||
          type.includes("complete") ||
          type.includes("failed") ||
          type === "session.snapshot")
      ) {
        const refreshedArtifacts = await loadProjectContent(
          activeProject,
          undefined,
          false,
        );
        if (type === "presentation.completed") {
          const rawDescriptorId = event.payload.descriptor_artifact_id;
          const descriptorArtifactId =
            typeof rawDescriptorId === "string" &&
            rawDescriptorId.trim().length > 0
              ? rawDescriptorId.replace(/^artifact:\/\//, "")
              : undefined;
          if (
            descriptorArtifactId &&
            refreshedArtifacts?.some(
              (artifact) => artifact.id === descriptorArtifactId,
            )
          ) {
            artifactDetailGenerationRef.current.set(
              descriptorArtifactId,
              (artifactDetailGenerationRef.current.get(descriptorArtifactId) ??
                0) + 1,
            );
            artifactDetailPendingRef.current.delete(descriptorArtifactId);
            setArtifactDetailStates((current) => {
              if (!current[descriptorArtifactId]) return current;
              const next = { ...current };
              delete next[descriptorArtifactId];
              return next;
            });
            setArtifactDetailErrors((current) => {
              if (!current[descriptorArtifactId]) return current;
              const next = { ...current };
              delete next[descriptorArtifactId];
              return next;
            });
            setArtifactDetails((current) => {
              if (!current[descriptorArtifactId]) return current;
              const next = { ...current };
              delete next[descriptorArtifactId];
              return next;
            });
            setArtifactOpenRequest({
              artifactId: descriptorArtifactId,
              requestId: ++artifactOpenRequestIdRef.current,
            });
          }
        }
      }
    },
    [
      activeProject,
      loadProjectContent,
      refreshOrganization,
      refreshLoadedResearchState,
      refreshProjectTurns,
      refreshSnapshot,
    ],
  );
  const handlePrimaryReplayGap = useCallback(
    (gap: SessionReplayGap) => {
      const project = activeProject;
      if (
        !project ||
        gap.sessionId !== project.sessionId ||
        project.projectId !== activeProjectIdRef.current
      ) {
        return;
      }
      cancelContentRetry();
      contentRequestRef.current += 1;
      // A replay snapshot without Artifact entries only says this stream
      // recovery did not carry the directory; it does not say that previously
      // confirmed scientific results were deleted. Keep those real objects
      // readable while the authoritative Artifact directory is fetched again.
      const preservesCachedArtifacts =
        gap.snapshot.artifacts.length === 0 &&
        listedArtifactsRef.current.length > 0;
      const nextArtifacts = preservesCachedArtifacts
        ? listedArtifactsRef.current
        : gap.snapshot.artifacts;
      setSnapshot({ ...gap.snapshot, artifacts: nextArtifacts });
      const durableEvents = organizationAgentEventsFromDurable(
        gap.snapshot.raw,
        project.projectId,
        project.sessionId,
      );
      setOrganizationAgentEvents(durableEvents);
      setListedArtifacts(nextArtifacts);
      listedArtifactsRef.current = nextArtifacts;
      if (!preservesCachedArtifacts) setArtifactDetails({});
      setOrganizationStatuses({});
      setScientificOperation(scientificOperationFromUnknown(gap.snapshot));
      setProjectContentState("ready");
      setProjectSnapshotError(undefined);
      // A replay gap has no trustworthy live ordering. Hydrate only the
      // server-provided durable projection, then let the primary session
      // stream resume the ordered live Organization events.
      void refreshOrganization(project.projectId);
      void loadProjectContent(
        project,
        { ...gap.snapshot, artifacts: nextArtifacts },
        false,
      );
    },
    [activeProject, cancelContentRetry, loadProjectContent, refreshOrganization],
  );

  const sessionStream = useSessionStream({
    api,
    sessionId,
    initialSeq: snapshot?.lastSeq ?? 0,
    enabled:
      !demoRequested &&
      Boolean(snapshot?.session?.running && sessionId),
    onEvent: handleEvent,
    onReplayGap: handlePrimaryReplayGap,
  });

  useEffect(() => {
    const project = activeProject;
    const activationPending = snapshot?.raw.activation_pending === true;
    if (!project || !activationPending || snapshot?.session?.running) {
      if (project) activationObservationAttemptsRef.current.delete(project.projectId);
      return undefined;
    }
    const attempts = activationObservationAttemptsRef.current.get(project.projectId) ?? 0;
    if (attempts >= 20) return undefined;
    // The POST already owns starting this research. These bounded GETs only
    // observe its durable receipt and live state, so a reload/reconnect can
    // never become a second model invocation or a hidden recovery command.
    const timer = window.setTimeout(() => {
      activationObservationAttemptsRef.current.set(project.projectId, attempts + 1);
      void refreshProjectTurns(project.projectId);
      void refreshSnapshot(
        project.projectId,
        project.sessionId,
        scopeGenerationRef.current,
      ).catch(() => undefined);
    }, 1_500);
    return () => window.clearTimeout(timer);
  }, [
    activeProject,
    refreshProjectTurns,
    refreshSnapshot,
    snapshot?.raw.activation_pending,
    snapshot?.session?.running,
  ]);

  const visibleProjectTurns = useMemo(
    () =>
      optimisticTurn &&
      !projectTurns.some(
        (turn) => turn.requestId === optimisticTurn.requestId,
      )
        ? [...projectTurns, optimisticTurn]
        : projectTurns,
    [optimisticTurn, projectTurns],
  );
  const latestProjectTurn = visibleProjectTurns.at(-1);
  const firstProjectTurn = visibleProjectTurns.find((turn) =>
    Boolean(turn.input.trim()),
  );
  const live = useMemo(
    () =>
      projectAdapterFromSnapshot(
        snapshot,
        projects,
        activeProjectId,
        firstProjectTurn?.input,
      ),
    [activeProjectId, firstProjectTurn?.input, projects, snapshot],
  );
  const organizationAgents = useMemo(
    () =>
      organization?.enabled
        ? organization.agents.map((agent, index) => {
            const observed = organizationStatuses[agent.id];
            const statusError = observed?.statusError ?? agent.statusError;
            return {
              id: agent.id,
              threadId: agent.threadId,
              name: agent.name,
              role: agent.role,
              btwAvailable: agent.btwAvailable && !organizationError,
              status: organizationStatus(
                observed?.status ?? agent.status,
                statusError,
              ),
              collaborationState: statusError
                ? undefined
                : collaborationStateFromOrganization({
                    status: observed?.status ?? agent.status,
                    liveWorker: agent.liveWorker,
                  }),
              summary: statusError
                ? "该 Agent 的运行状态暂时无法读取。"
                : safeCollaborationPreview(
                      observed?.recentSummaries.at(-1) ??
                        agent.recentSummaries.at(-1),
                    ) ??
                  (agent.liveWorker
                    ? "运行时摘要未公开"
                    : "当前 Agent 未启动"),
              accent: organizationAgentAccent(index),
            };
          })
        : [],
    [organization, organizationError, organizationStatuses],
  );
  const activeProjectTurnId = useMemo(
    () =>
      resolveActiveProjectTurnId(
        snapshot?.activeTurnId,
        visibleProjectTurns,
        organizationAgentEvents,
      ),
    [organizationAgentEvents, snapshot?.activeTurnId, visibleProjectTurns],
  );
  const projectRuntimeReady = snapshot?.session?.running === true;
  const liveResearchActive =
    !demoRequested && Boolean(organization?.enabled) && projectRuntimeReady;
  const collaborationModel = useMemo(
    () =>
      buildCadenceCollaborationModel({
        agents: organizationAgents,
        events: organizationAgentEvents,
        projectId: activeProjectId ?? "",
      }),
    [activeProjectId, organizationAgentEvents, organizationAgents],
  );
  const runtimeArtifactSources = [
    ...(live.artifacts ?? []),
    ...listedArtifacts.map(cadenceArtifactFromRecord),
  ];
  const runtimeArtifacts = [
    ...new Map(
      runtimeArtifactSources.map((artifact) => [artifact.id, artifact]),
    ).values(),
  ].map((artifact) => {
    const listed = listedArtifacts.find((record) => record.id === artifact.id);
    const nextArtifact = listed
      ? cadenceArtifactFromRecord(listed)
      : artifactWithAuthoritativeTimestamp(artifact);
    return {
      ...nextArtifact,
      path: nextArtifact.path ?? listed?.path,
    };
  }).filter((artifact) => !isInternalResearchStateArtifact(artifact));
  const baseArtifacts = demoRequested
    ? (demo.artifacts ?? [])
    : runtimeArtifacts;
  const artifacts = baseArtifacts.map(
    (artifact) => artifactDetails[artifact.id] ?? artifact,
  );
  const adapter = demoRequested
    ? {
        ...demo,
        composerCapabilities: composerCapabilities,
        availableAttachments: availableAttachments.map(toCadenceAttachment),
        attachments: attachments.map(toCadenceAttachment),
        uploading,
        uploadError,
        btwResponse,
      }
    : {
        ...live,
        mode: "live" as const,
        projects: live.projects ?? [],
        agents: organization?.enabled
          ? collaborationModel.agents
          : (live.agents ?? EMPTY_CADENCE_AGENTS),
        agentEvents: organizationAgentEvents,
        liveResearchActive,
        researchStartedAt: timestampMs(latestProjectTurn?.createdAt),
        sessionRunning: snapshot?.session?.running ?? false,
        projectRuntimeReady,
        streamState: sessionStream.state,
        collaboration:
          organization?.enabled && activeProjectId
            ? {
                projectId: activeProjectId,
                interactions: collaborationModel.interactions,
                // Provider child threads have their own read-only timeline.
                // Pass the discovered IDs through so parent replies never
                // flatten a child Agent's public output into the root turn.
                delegatedAgents: collaborationModel.delegatedAgents,
              }
            : undefined,
        residues: live.residues ?? [],
        artifacts,
        composerCapabilities,
        availableAttachments: availableAttachments.map(toCadenceAttachment),
        attachments: attachments.map(toCadenceAttachment),
        uploading,
        uploadError,
        btwResponse,
      };

  const createProject = useCallback(
    async (input: { name: string; question?: string }) => {
      setCreatingProject(true);
      setCreateProjectError(undefined);
      setProjectNotice(undefined);
      try {
        const created = await api.createProject(input);
        // A late project-list response can predate this Project and would
        // otherwise restore the previous active Project after selectProject.
        // Invalidate it so a just-created upload and its Runtime context stay
        // bound to the Project the researcher explicitly created.
        projectListRequestRef.current += 1;
        if (created.snapshot) {
          seededSnapshotsRef.current.set(
            created.project.projectId,
            created.snapshot,
          );
        }
        const nextProjects = [
          created.project,
          ...projectsRef.current.filter(
            (project) => project.projectId !== created.project.projectId,
          ),
        ];
        replaceProjects(nextProjects);
        setProjectNotice(
          input.question?.trim()
            ? "项目已创建，研究团队正在启动。你可以先查看已有研究记录与产物。"
            : "项目已创建。添加研究问题后，Cadence 才会开始研究。",
        );
        selectProject(created.project.projectId);
        setProjectListState("ready");
      } catch (cause) {
        setCreateProjectError(
          "创建结果暂不明确。请刷新项目列表确认项目是否已创建，避免重复提交。",
        );
        void loadProjects();
        throw cause;
      } finally {
        setCreatingProject(false);
      }
    },
    [loadProjects, replaceProjects, selectProject],
  );

  const importProject = useCallback(
    async (input: { selectionId: string; mode: "copy" | "in_place"; name?: string }) => {
      setCreatingProject(true);
      setCreateProjectError(undefined);
      setProjectNotice(undefined);
      try {
        const created = await api.importProject(input);
        projectListRequestRef.current += 1;
        const nextProjects = [
          created.project,
          ...projectsRef.current.filter(
            (project) => project.projectId !== created.project.projectId,
          ),
        ];
        replaceProjects(nextProjects);
        setProjectNotice(
          input.mode === "in_place"
            ? "已在原位置打开项目。Cadence 仅保存项目绑定；删除项目不会删除原文件夹。"
            : "已复制到 Cadence 管理的项目工作区。原文件夹未被修改。",
        );
        selectProject(created.project.projectId);
        setProjectListState("ready");
      } catch (cause) {
        setCreateProjectError(errorMessage(cause, "导入项目失败，请重新选择文件夹。"));
        void loadProjects();
        throw cause;
      } finally {
        setCreatingProject(false);
      }
    },
    [loadProjects, replaceProjects, selectProject],
  );

  const sendPrompt = useCallback(
    async (
      input: string,
      context: CadenceContext,
      cadenceAttachments: CadenceAttachment[],
      intent: CadenceComposerIntent,
      composerReferences: CadenceComposerReference[],
    ) => {
      if (
        !activeProject ||
        !sessionId ||
        context.projectId !== activeProject.projectId
      ) {
        throw new Error("请先选择或创建项目，再发送研究请求。");
      }
      setRequestError(undefined);
      let currentOrganization = organization;
      if (!currentOrganization || organizationError) {
        const refreshedOrganization = await refreshOrganization(
          activeProject.projectId,
        );
        currentOrganization = refreshedOrganization ?? (
          organizationError ? undefined : currentOrganization
        );
        // Organization activation is best effort. A project still has a
        // session turn route, so a transient organization outage must not
        // make the composer unable to submit the user's research request.
        if (
          currentOrganization?.enabled &&
          !currentOrganization.agents.length
        ) {
          currentOrganization = undefined;
        }
      }
      const controlledCommand = controlledCommandInput(input);
      const explicitAgentCandidate =
        controlledCommand?.agentId ??
        input.match(/(?:^|\s)@([a-z0-9_-]+)/i)?.[1];
      // `@` also introduces the Composer's Project Input/Artifact picker.
      // Only an exact live roster ID is an Agent route; an unresolved mention
      // remains user-authored research text instead of a false routing error.
      const explicitAgent = currentOrganization?.agents.some(
        (agent) => agent.id === explicitAgentCandidate,
      )
        ? explicitAgentCandidate
        : undefined;
      if (
        intent.kind === "controlled_command" &&
        controlledCommand?.agentId &&
        !explicitAgent
      ) {
        // A visible `@Agent !command` is an explicit researcher instruction.
        // Unlike an ordinary unresolved mention, it must not fall through to a
        // root command and run under a different Agent's Sandbox authority.
        throw new Error(
          `找不到受控执行 Agent @${controlledCommand.agentId}；请从 @ 列表中选择已授权 Agent。`,
        );
      }
      if (
        intent.kind === "continue_research" &&
        explicitAgent &&
        explicitAgent !== intent.targetAgentId
      ) {
        throw new Error("继续研究已绑定指定专家，不能同时用 @ 指向另一位专家。");
      }
      const routeAgent =
        intent.kind === "continue_research"
          ? intent.targetAgentId
          : explicitAgent ?? context.agentId;
      const wireIntent = composerIntentRequest(intent, input);
      const wireReferences = composerReferenceRequests(composerReferences);
      if (
        intent.kind === "controlled_command" &&
        !(currentOrganization?.enabled && currentOrganization.acceptingInput)
      ) {
        throw new Error(
          "受控命令需要当前项目可用的 Organization Runtime；不会回退为浏览器或本机 shell。",
        );
      }
      const controlledCapability = composerCapabilities.find(
        (capability) => capability.kind === "controlled_command",
      );
      if (
        intent.kind === "controlled_command" &&
        controlledCapability?.requiresAgentSelection &&
        !routeAgent
      ) {
        throw new Error(
          "当前有多个受控 OpenSandbox 执行 Agent；请用 @ 明确选择执行 Agent。",
        );
      }
      const contextLine = cadenceContextLine(
        activeProject,
        context,
        routeAgent,
      );
      const operationRequest = scientificOperationRequest(
        intent.kind === "controlled_command"
          ? controlledCommand?.command.trim() ?? ""
          : input,
        context,
        routeAgent,
      );
      const organizationTarget =
        currentOrganization?.enabled &&
        currentOrganization.acceptingInput &&
        routeAgent &&
        currentOrganization.agents.some((agent) => agent.id === routeAgent)
          ? routeAgent
          : undefined;
      const organizationRoot =
        currentOrganization?.enabled &&
        currentOrganization.acceptingInput &&
        !routeAgent;
      cadenceAttachments.forEach((attachment) => {
        if (
          attachment.kind === "upload" &&
          (attachment.projectId !== activeProject.projectId ||
            attachment.sessionId !== activeProject.sessionId)
        ) {
          throw new Error(
            `附件 ${attachment.name} 不属于当前项目，已拒绝发送。`,
          );
        }
      });
      const attachmentRefs = cadenceAttachments.map((attachment) => ({
        id: attachment.id,
        name: attachment.name,
        kind: attachment.kind ?? "upload",
        path: attachment.path,
        projectId: attachment.projectId,
        sessionId: attachment.sessionId,
        workspacePath: attachment.workspacePath,
        mimeType: attachment.mimeType,
        size: attachment.size,
        documentSelection: attachment.documentSelection,
      }));
      if (intent.kind === "continue_research" && attachmentRefs.length) {
        throw new Error(
          "继续研究目前不支持添加附件；请移除附件后重新提交为新的项目研究请求。",
        );
      }
      if (intent.kind === "continue_research" && !organizationTarget) {
        throw new Error("继续研究需要当前项目中可用的指定专家。");
      }
      if (organizationTarget && attachmentRefs.length) {
        throw new Error(
          "当前 Organization Agent 定向消息不支持附件，请改用 Organization root。",
        );
      }
      const displayInput =
        input.trim() ||
        (attachmentRefs.length
          ? `基于 ${attachmentRefs.length} 个附件的研究请求`
          : "研究请求");
      const requestId = `cadence-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const optimistic: ProjectTurn = {
        id: `optimistic-${requestId}`,
        requestId,
        projectId: activeProject.projectId,
        kind: organizationTarget
          ? "organization_message"
          : organizationRoot
            ? "organization_root"
            : "session_turn",
        input: displayInput,
        status: "pending",
        createdAt: Date.now(),
        attachments: attachmentRefs,
        intent: publicProjectTurnIntent(intent),
        routedAgentIds: organizationTarget ? [organizationTarget] : [],
        activityIds: [],
        artifactIds: [],
        evidenceIds: [],
        steeredInputs: [],
        raw: {},
      };
      setOptimisticTurn(optimistic);
      setTurnsError(undefined);
      setTurnsErrorOrigin(undefined);
      let submissionAccepted = false;
      try {
        if (intent.kind === "continue_research") {
          await api.sendOrganizationMessage(intent.targetAgentId, {
            project_id: activeProject.projectId,
            request_id: requestId,
            content: `${operationRequest}\n\n[Cadence context] ${contextLine}`,
            display_input: displayInput,
            intent: {
              kind: "continue_research",
              parent_request_id: intent.parentRequestId,
            },
            references: wireReferences,
            need_reply: false,
          });
          submissionAccepted = true;
        } else if (organizationTarget) {
          await api.sendOrganizationMessage(organizationTarget, {
            project_id: activeProject.projectId,
            request_id: requestId,
            content: `${operationRequest}\n\n[Cadence context] ${contextLine}`,
            display_input: displayInput,
            intent: wireIntent,
            references: wireReferences,
            need_reply: false,
          });
          submissionAccepted = true;
        } else if (organizationRoot) {
          await api.sendOrganizationRoot({
            project_id: activeProject.projectId,
            request_id: requestId,
            content: `${operationRequest}\n\n[Cadence context] ${contextLine}`,
            display_input: displayInput,
            attachments: attachmentRefs,
            intent: wireIntent,
            references: wireReferences,
            need_reply: false,
          });
          submissionAccepted = true;
        } else {
          const result = await api.sendTurn(sessionId, {
            request_id: requestId,
            input: `${operationRequest}\n\n[Cadence context] ${contextLine}`,
            display_input: displayInput,
            attachments: attachmentRefs,
            intent: wireIntent,
            references: wireReferences,
          });
          submissionAccepted = true;
          const nextScientificOperation =
            scientificOperationFromUnknown(result);
          if (nextScientificOperation)
            setScientificOperation(nextScientificOperation);
        }
        setOptimisticTurn((current) =>
          current?.requestId === requestId
            ? { ...current, status: "running" }
            : current,
        );
        await Promise.all([
          refreshProjectTurns(activeProject.projectId),
          organizationTarget || organizationRoot
            ? refreshOrganization(activeProject.projectId)
            : Promise.resolve(undefined),
          // A successful Organization-root POST owns Runtime activation. Read
          // the resulting server state so the browser can attach to its real
          // SSE stream; without this observation the right Artifact panel
          // would need a reload to discover a publication already in flight.
          organizationRoot
            ? refreshSnapshot(
                activeProject.projectId,
                sessionId,
                scopeGenerationRef.current,
              ).catch(() => false)
            : Promise.resolve(undefined),
        ]);
      } catch (cause) {
        const message = errorMessage(cause, "研究请求发送失败，请重试。");
        let canonical: ProjectTurn | undefined;
        const isCurrentProject = () =>
          activeProject.projectId === activeProjectIdRef.current;
        try {
          const page = await api.listProjectTurns?.(activeProject.projectId);
          canonical = page?.turns.find((turn) => turn.requestId === requestId);
          if (canonical && isCurrentProject()) {
            setProjectTurns((current) =>
              mergeRefreshedProjectTurns(current, [canonical as ProjectTurn]),
            );
            setOptimisticTurn((current) =>
              current?.requestId === requestId ? undefined : current,
            );
          }
        } catch {
          // The submission result remains unknown if reconciliation is down.
        }
        if (canonical && canonical.status !== "error") {
          if (isCurrentProject()) setRequestError(undefined);
          return;
        }
        if (canonical) {
          const canonicalMessage = canonical.error || message;
          if (isCurrentProject()) {
            setRequestError(canonicalMessage);
          }
          throw cause;
        }
        if (!submissionAccepted && cause instanceof ApiError) {
          /*
           * Before admission resolves, ApiError is an explicit HTTP outcome
           * (including pre-admission 5xx parser/catalog failures), so preserve
           * its server message and finish this optimistic row as an error.
           * Once admission succeeded, later ApiError values are reconciliation
           * failures; those stay ambiguous because retrying could duplicate work.
           */
          if (isCurrentProject()) {
            setRequestError(message);
            setOptimisticTurn((current) =>
              current?.requestId === requestId
                ? { ...current, status: "error", error: message }
                : current,
            );
          }
          throw cause;
        }
        if (isCurrentProject()) {
          setRequestError(
            "请求提交状态未确认。请稍后刷新请求历史；请勿重复提交。",
          );
          setOptimisticTurn((current) =>
            current?.requestId === requestId
              ? {
                  ...current,
                  status: "pending",
                  error: undefined,
                  submissionUncertain: true,
                }
              : current,
          );
        }
        return;
      }
    },
    [
      activeProject,
      organization,
      organizationError,
      refreshOrganization,
      refreshProjectTurns,
      refreshSnapshot,
      sessionId,
    ],
  );

  const steerPrompt = useCallback(
    async (parentRequestId: string, input: string) => {
      const project = activeProject;
      const content = input.trim();
      if (!project || !content) {
        throw new Error("请先输入要发送给当前 Lead 的指令。");
      }
      const parent = visibleProjectTurns.find(
        (turn) => turn.requestId === parentRequestId,
      );
      if (
        !parent ||
        parent.kind !== "organization_root" ||
        !parent.primaryAgentId ||
        parent.status !== "running"
      ) {
        throw new Error(
          "当前研究请求已不再接受追加指令；请等待刷新后新建请求。",
        );
      }
      const followUpId = `cadence-steer-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      await api.steerOrganizationProjectTurn(project.projectId, parentRequestId, {
        content,
        follow_up_id: followUpId,
      });
      await Promise.all([
        refreshProjectTurns(project.projectId),
        refreshOrganization(project.projectId),
      ]);
    },
    [
      activeProject,
      refreshOrganization,
      refreshProjectTurns,
      visibleProjectTurns,
    ],
  );

  const decidePermission = useCallback(
    async (requestId: string, decision: "approve" | "deny") => {
      const project = activeProject;
      const currentSessionId = sessionId;
      const scopeGeneration = scopeGenerationRef.current;
      const request: PermissionRequest | undefined = snapshot?.permissions.find(
        (item) => item.requestId === requestId,
      );
      if (
        !project ||
        !currentSessionId ||
        !request ||
        project.projectId !== activeProjectIdRef.current
      ) {
        throw new Error("权限请求已失效，请刷新当前项目后重试。");
      }
      if (permissionPendingRef.current.has(requestId)) return;
      permissionPendingRef.current.add(requestId);
      setPermissionError(undefined);
      try {
        await api.decidePermission(currentSessionId, requestId, decision);
        await refreshSnapshot(
          project.projectId,
          currentSessionId,
          scopeGeneration,
        );
      } catch (cause) {
        if (
          scopeGeneration === scopeGenerationRef.current &&
          project.projectId === activeProjectIdRef.current
        ) {
          setPermissionError(errorMessage(cause, "权限决策提交失败。"));
        }
        throw cause;
      } finally {
        permissionPendingRef.current.delete(requestId);
      }
    },
    [activeProject, refreshSnapshot, sessionId, snapshot],
  );

  const cancelTurn = useCallback(
    async (turnId: string) => {
      const project = activeProject;
      const currentSessionId = sessionId;
      const scopeGeneration = scopeGenerationRef.current;
      if (
        !project ||
        !currentSessionId ||
        project.projectId !== activeProjectIdRef.current
      ) {
        throw new Error("取消请求已失效，请刷新当前项目后重试。");
      }
      if (cancelPendingRef.current.has(turnId)) return;
      cancelPendingRef.current.add(turnId);
      setRequestError(undefined);
      try {
        await api.cancelTurn(currentSessionId, turnId);
        const completedAt = Date.now();
        setOptimisticTurn((current) =>
          current?.id === turnId
            ? { ...current, status: "cancelled", completedAt }
            : current,
        );
        setProjectTurns((current) =>
          current.map((turn) =>
            turn.id === turnId &&
            (turn.status === "pending" || turn.status === "running")
              ? { ...turn, status: "cancelled", completedAt }
              : turn,
          ),
        );
        await refreshSnapshot(
          project.projectId,
          currentSessionId,
          scopeGeneration,
        );
      } catch (cause) {
        if (
          scopeGeneration === scopeGenerationRef.current &&
          project.projectId === activeProjectIdRef.current
        ) {
          setRequestError(errorMessage(cause, "停止请求失败。"));
        }
        throw cause;
      } finally {
        cancelPendingRef.current.delete(turnId);
      }
    },
    [activeProject, refreshSnapshot, sessionId],
  );

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!files.length) return;
      /*
       * A file chooser can fire after createProject confirms a new Project but
       * before React commits the render that replaces this callback. Resolve
       * the route scope from refs updated by that confirmation, not from a
       * render-captured Project, so a durable upload can never be posted to
       * the Project that happened to be visible one render earlier.
       */
      const projectId = activeProjectIdRef.current;
      const project = projectId
        ? projectsRef.current.find(
            (candidate) => candidate.projectId === projectId,
          )
        : undefined;
      const currentSessionId = project?.sessionId;
      if (!project || !currentSessionId) {
        setUploadError("请先选择或创建项目，再上传文件。");
        failedUploadFilesRef.current = files;
        return;
      }
      const scopeGeneration = scopeGenerationRef.current;
      failedUploadFilesRef.current = files;
      setUploading(true);
      setUploadError(undefined);
      const processUploaded = (uploaded: AttachmentRef[], keepRetryFiles: boolean) => {
        if (!uploaded.length) return;
        const preparing = uploaded.map((attachment) => ({
          ...attachment,
          inspection: {
            version: attachment.inspection?.version ?? 1,
            status: "preparing" as const,
            raw: {
              ...(attachment.inspection?.raw ?? {}),
              status: "preparing",
            },
          },
        }));
        setAvailableAttachments((current) =>
          mergeAttachments(current, preparing),
        );
        setAttachments((current) => mergeAttachments(current, preparing));
        if (!keepRetryFiles) {
          failedUploadFilesRef.current = [];
        }
        void Promise.all(
          preparing.map(async (attachment) => {
            if (!attachment.id) return undefined;
            let prepared: AttachmentRef;
            let explicitError: ApiError | undefined;
            try {
              prepared = await api.prepareUpload(currentSessionId, attachment.id);
            } catch (cause) {
              if (cause instanceof ApiError) {
                /*
                 * An HTTP response is an authoritative server outcome: the
                 * upload is durable, but preparation failed or was rejected.
                 * Keep that distinction visible so a parser/configuration
                 * error is not mislabeled as a transport ambiguity. Only a
                 * no-response failure may use the "待确认" state below.
                 */
                explicitError = cause;
                prepared = {
                  ...attachment,
                  inspection: {
                    version: 1,
                    status: "failed",
                    reason: {
                      code: "prepare_http_error",
                      message: cause.message || "结构化视图准备失败。",
                    },
                    raw: {
                      ...(attachment.inspection?.raw ?? {}),
                      status: "failed",
                      reason: {
                        code: "prepare_http_error",
                        message: cause.message || "结构化视图准备失败。",
                      },
                    },
                  },
                };
              } else {
                // The raw Project Input is durable; a lost prepare response
                // must not be shown as a confirmed parsing failure.
                prepared = {
                  ...attachment,
                  inspection: {
                    version: 1,
                    status: "stored",
                    reason: {
                      code: "prepare_request_unconfirmed",
                      message: "无法确认结构化副本是否已准备。",
                    },
                    raw: { status: "stored" },
                  },
                };
              }
            }
            if (
              scopeGeneration !== scopeGenerationRef.current ||
              projectId !== activeProjectIdRef.current
            ) {
              return explicitError;
            }
            setAttachments((current) =>
              refreshSelectedAttachments(current, [prepared]),
            );
            setAvailableAttachments((current) =>
              mergeAttachments(current, [prepared]),
            );
            return explicitError;
          }),
        ).then((errors) => {
          const explicitError = errors.find(
            (error): error is ApiError => error instanceof ApiError,
          );
          if (
            explicitError &&
            scopeGeneration === scopeGenerationRef.current &&
            projectId === activeProjectIdRef.current
          ) {
            setUploadError(errorMessage(explicitError, "文件解析失败。"));
          }
        });
      };
      try {
        const uploaded = await api.uploadFiles(currentSessionId, files);
        if (
          scopeGeneration !== scopeGenerationRef.current ||
          projectId !== activeProjectIdRef.current
        ) {
          return;
        }
        processUploaded(uploaded, false);
      } catch (cause) {
        if (
          scopeGeneration === scopeGenerationRef.current &&
          projectId === activeProjectIdRef.current
        ) {
          if (cause instanceof UploadFilesError) {
            failedUploadFilesRef.current = cause.failedFiles;
            processUploaded(cause.uploaded, true);
          }
          setUploadError(errorMessage(cause, "文件上传失败。"));
        }
      } finally {
        if (
          scopeGeneration === scopeGenerationRef.current &&
          projectId === activeProjectIdRef.current
        ) {
          setUploading(false);
        }
      }
    },
    [],
  );

  const removeAttachment = useCallback((attachmentId: string) => {
    setAttachments((current) =>
      current.filter((attachment) => attachment.id !== attachmentId),
    );
  }, []);

  const attachProjectInput = useCallback(
    (attachmentId: string) => {
      const source = availableAttachments.find(
        (attachment) => attachment.id === attachmentId,
      );
      if (!source) {
        setUploadError("该项目文件已不可用；请重新加载项目后再引用。");
        return;
      }
      setAttachments((current) =>
        current.some((attachment) => attachment.id === source.id)
          ? current
          : [...current, source],
      );
    },
    [availableAttachments],
  );

  const handleBTW = useCallback(
    async (agentId: string, input: string, context: CadenceContext) => {
      if (
        !activeProject ||
        !sessionId ||
        context.projectId !== activeProject.projectId
      ) {
        throw new Error("请先选择或创建项目，再发送 BTW。");
      }
      const scopeGeneration = scopeGenerationRef.current;
      const projectId = activeProject.projectId;
      const requestId = `cadence-btw-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const requestInput = [
        `[Cadence BTW] @${agentId} ${input}`,
        `[Cadence context] ${cadenceContextLine(activeProject, context, agentId)}`,
      ].join("\n\n");
      const currentOrganization = await refreshOrganization(projectId);
      const currentAgent = currentOrganization?.agents.find(
        (agent) => agent.id === agentId,
      );
      if (
        !currentOrganization?.enabled ||
        !currentOrganization.acceptingInput ||
        !currentAgent?.liveWorker ||
        !currentAgent.btwAvailable
      ) {
        throw new Error(
          "当前 Agent 没有可用的独立 BTW side channel，请等待其活动任务恢复后重试。",
        );
      }
      if (
        scopeGeneration === scopeGenerationRef.current &&
        projectId === activeProjectIdRef.current
      ) {
        setBtwResponse({ status: "loading", agentId, requestId });
      }
      try {
        const result = await api.sendOrganizationBTW(agentId, {
          project_id: projectId,
          request_id: requestId,
          content: requestInput,
        });
        if (
          scopeGeneration !== scopeGenerationRef.current ||
          projectId !== activeProjectIdRef.current
        ) {
          return;
        }
        setBtwResponse({
          status: "completed",
          agentId,
          requestId: result.request_id,
          text: result.answer.text,
          source: result.answer.source,
        });
      } catch (cause) {
        const message = errorMessage(cause, "BTW 独立回答获取失败。");
        if (/unavailable|不可用/u.test(message)) {
          await refreshOrganization(projectId);
        }
        if (
          scopeGeneration === scopeGenerationRef.current &&
          projectId === activeProjectIdRef.current
        ) {
          setBtwResponse({
            status: "error",
            agentId,
            requestId,
            error: message,
          });
        }
        throw cause;
      }
    },
    [activeProject, refreshOrganization, sessionId],
  );

  const handleArtifactDetailRequest = useCallback(
    async (artifactId: string) => {
      if (
        demoRequested ||
        !activeProject ||
        !sessionId ||
        artifactDetails[artifactId] ||
        artifactDetailPendingRef.current.has(artifactId)
      )
        return;
      const listed = listedArtifacts.find(
        (artifact) => artifact.id === artifactId,
      );
      if (!listed) return;
      const existing =
        baseArtifacts.find((artifact) => artifact.id === artifactId) ??
        cadenceArtifactFromRecord(listed);
      const scopeGeneration = scopeGenerationRef.current;
      const detailGeneration =
        artifactDetailGenerationRef.current.get(artifactId) ?? 0;
      artifactDetailPendingRef.current.add(artifactId);
      setArtifactDetailStates((current) => ({
        ...current,
        [artifactId]: "loading",
      }));
      setArtifactDetailErrors((current) => {
        if (!current[artifactId]) return current;
        const next = { ...current };
        delete next[artifactId];
        return next;
      });
      try {
        const detail = await api.getArtifact(sessionId, artifactId);
        if (
          scopeGeneration !== scopeGenerationRef.current ||
          activeProject.projectId !== activeProjectIdRef.current ||
          detailGeneration !==
            (artifactDetailGenerationRef.current.get(artifactId) ?? 0)
        ) {
          return;
        }
        const ownerAgentId =
          existing.owner && existing.owner !== "unknown"
            ? existing.owner
            : undefined;
        if (
          ownerAgentId &&
          !organization?.agents.some((agent) => agent.id === ownerAgentId)
        ) {
          await refreshOrganization(activeProject.projectId);
        }
        /*
         * Artifact detail is the direct file body. Keep list metadata as the
         * only presentation authority so a JSON table or decision body cannot
         * be mistaken for an Artifact envelope and rename or reclassify itself.
         */
        const artifactMimeType = existing.mimeType ?? detail.mimeType;
        const detailMimeType = normalizeMimeType(artifactMimeType);
        const binaryImageUrl =
          detail.blob && detailMimeType?.startsWith("image/")
            ? api.artifactUrl(sessionId, artifactId)
            : undefined;
        const publishedPresentation = existing.publishedPresentation;
        const publishedContent = publishedArtifactContentFromRaw(
          detail.raw,
          publishedPresentation?.format,
        ) ?? publishedArtifactContentFromDirectFile(
          detail.content,
          publishedPresentation?.format,
        );
        const previewKind = previewKindFromDetail(
          artifactMimeType,
          existing.name,
          detail.content,
          existing.kind,
          publishedPresentation?.format,
        );
        const structureFormat = structureFormatFromDetail(
          artifactMimeType,
          existing.name,
          existing.kind,
        );
        const scientificDocument = scientificDocumentFromRaw(detail.raw);
        const localScientificDocument = scientificDocument
          ? undefined
          : localScientificDocumentFromRaw(detail.raw);
        const rendererDocument =
          publishedPresentation?.format === "html"
            ? undefined
            : await resolveRendererDocument(
                detail.raw,
                listed.path,
                listedArtifacts,
                sessionId,
              );
        if (
          scopeGeneration !== scopeGenerationRef.current ||
          activeProject.projectId !== activeProjectIdRef.current ||
          detailGeneration !==
            (artifactDetailGenerationRef.current.get(artifactId) ?? 0)
        ) {
          return;
        }
        setArtifactDetails((current) => ({
          ...current,
          [artifactId]: {
            ...existing,
            id: artifactId,
            path: existing.path ?? listed.path,
            kind: existing.kind,
            mimeType: artifactMimeType,
            name: existing.name,
            type:
              existing.type ??
              previewTypeFromDetail(
                artifactMimeType,
                existing.name,
                existing.kind,
                publishedPresentation?.format,
              ),
            description:
              publishedPresentation?.summary ??
              existing.description,
            updatedAt: existing.updatedAt,
            previewUrl:
              detail.previewUrl ?? existing.previewUrl ?? binaryImageUrl,
            downloadUrl:
              detail.downloadUrl ??
              existing.downloadUrl ??
              api.artifactUrl(sessionId, artifactId),
            owner: existing.owner,
            sourceRevision:
              existing.sourceRevision ??
              revisionFromRaw(listed.raw, [
                "source_revision",
                "artifact_revision",
                "revision",
              ]),
            descriptorRevision:
              existing.descriptorRevision ??
              revisionFromRaw(listed.raw, ["descriptor_revision"]),
            versions: scientificDocument ?? localScientificDocument
              ? artifactVersionsFromScientific(
                  (scientificDocument ?? localScientificDocument)!,
                )
              : existing.versions,
            provenance: scientificDocument ?? localScientificDocument
              ? artifactProvenanceFromScientific(
                  (scientificDocument ?? localScientificDocument)!,
                )
              : existing.provenance,
            scientificDocument,
            localScientificDocument,
            rendererDocument,
            publishedPresentation,
            publishedContent,
            preview:
              !scientificDocument &&
              !localScientificDocument &&
              !rendererDocument &&
              detail.content
                ? {
                    kind: previewKind,
                    content: detail.content,
                    format:
                      previewKind === "structure"
                        ? structureFormat
                        : undefined,
                  }
                : undefined,
          },
        }));
        setArtifactDetailStates((current) => ({
          ...current,
          [artifactId]: "ready",
        }));
      } catch (cause) {
        if (
          scopeGeneration === scopeGenerationRef.current &&
          activeProject.projectId === activeProjectIdRef.current &&
          detailGeneration ===
            (artifactDetailGenerationRef.current.get(artifactId) ?? 0)
        ) {
          setArtifactDetailStates((current) => ({
            ...current,
            [artifactId]: "error",
          }));
          setArtifactDetailErrors((current) => ({
            ...current,
            [artifactId]: errorMessage(cause, "产物详情加载失败。"),
          }));
        }
      } finally {
        if (
          detailGeneration ===
          (artifactDetailGenerationRef.current.get(artifactId) ?? 0)
        ) {
          artifactDetailPendingRef.current.delete(artifactId);
        }
      }
    },
    [
      activeProject,
      artifactDetails,
      baseArtifacts,
      demoRequested,
      listedArtifacts,
      organization,
      refreshOrganization,
      sessionId,
    ],
  );

  const retryArtifactDetail = useCallback(
    (artifactId: string) => {
      setArtifactDetailStates((current) => {
        if (!current[artifactId]) return current;
        const next = { ...current };
        delete next[artifactId];
        return next;
      });
      setArtifactDetailErrors((current) => {
        if (!current[artifactId]) return current;
        const next = { ...current };
        delete next[artifactId];
        return next;
      });
      void handleArtifactDetailRequest(artifactId);
    },
    [handleArtifactDetailRequest],
  );

  const openResearchStateArtifact = useCallback(
    (reference: string) => {
      const artifactId = reference.replace(/^artifact:\/\//, "");
      const artifact = listedArtifacts.find(
        (item) => item.id === artifactId || item.path === artifactId,
      );
      if (!artifact || isInternalResearchStateArtifact(artifact)) {
        setRequestError(`无法在当前项目中打开证据 Artifact：${reference}`);
        return;
      }
      setArtifactOpenRequest({
        artifactId: artifact.id,
        requestId: ++artifactOpenRequestIdRef.current,
      });
    },
    [listedArtifacts],
  );

  const applyScientificArtifact = useCallback(
    async (
      artifactId: string,
      baseRevision: number,
      document: ScientificArtifactDocument,
    ) => {
      if (demoRequested || !activeProject || !sessionId) {
        throw new Error("请先选择 live 项目，再应用科学产物修改。");
      }
      if (!api.updateArtifact) {
        throw new Error("当前 API client 不支持 Artifact 更新。");
      }
      const raw = await api.updateArtifact(sessionId, artifactId, {
        base_revision: baseRevision,
        document,
      });
      const updatedDocument = scientificDocumentFromRaw(raw);
      if (!updatedDocument) {
        throw new Error("Artifact 更新响应缺少有效的科学文档。");
      }
      setArtifactDetails((current) => {
        const existing =
          current[artifactId] ??
          baseArtifacts.find((artifact) => artifact.id === artifactId);
        return {
          ...current,
          [artifactId]: {
            ...existing,
            id: artifactId,
            name: existing?.name ?? artifactId,
            type: existing?.type ?? "Data",
            description:
              stringFromRaw(updatedDocument, "description") ??
              existing?.description,
            updatedAt: existing?.updatedAt,
            owner: existing?.owner ?? null,
            versions: artifactVersionsFromScientific(updatedDocument),
            provenance: artifactProvenanceFromScientific(updatedDocument),
            scientificDocument: updatedDocument,
            preview: undefined,
          },
        };
      });
      setScientificOperation(undefined);
      return updatedDocument;
    },
    [activeProject, baseArtifacts, demoRequested, sessionId],
  );

  const applyReportTextReplacement = useCallback(
    async (proposal: ReportTextReplacementProposal) => {
      if (demoRequested || !activeProject || !sessionId) {
        throw new Error("请先选择 live 项目，再应用报告文本修改。");
      }
      const result = await api.applyReportTextReplacement(
        sessionId,
        proposal.artifact_id,
        {
          base_content_sha256: proposal.base_content_sha256,
          selection: proposal.selection as unknown as Record<string, unknown>,
          replacement: proposal.replacement,
        },
      );
      setListedArtifacts((current) =>
        current.map((artifact) =>
          artifact.id === proposal.artifact_id
            ? {
                ...artifact,
                contentSha256: result.contentSha256,
                raw: {
                  ...artifact.raw,
                  content_sha256: result.contentSha256,
                },
              }
            : artifact,
        ),
      );
      setArtifactDetails((current) => {
        const existing =
          current[proposal.artifact_id] ??
          baseArtifacts.find((artifact) => artifact.id === proposal.artifact_id);
        if (!existing) return current;
        return {
          ...current,
          [proposal.artifact_id]: {
            ...existing,
            contentSha256: result.contentSha256,
            updatedAt: undefined,
            preview: {
              kind:
                existing.preview?.kind ??
                previewKindFromDetail(
                  existing.mimeType,
                  existing.name,
                  result.content,
                  existing.kind,
                  existing.publishedPresentation?.format,
                ),
              content: result.content,
              format: existing.preview?.format,
            },
          },
        };
      });
      return result;
    },
    [activeProject, baseArtifacts, demoRequested, sessionId],
  );

  const sendArtifactInquiry = useCallback(
    async ({
      artifact,
      revision,
      selection,
      intent,
      input,
    }: ArtifactInquirySubmit) => {
      if (demoRequested || !activeProject) {
        throw new Error("请先选择 live 项目，再围绕 Artifact 提问。");
      }
      const requestText = input.trim();
      if (!requestText) throw new Error("请输入要发送给产物 Owner 的请求。");
      const ownerAgentId = artifact.owner?.trim();
      if (!ownerAgentId || ownerAgentId === "unknown") {
        throw new Error("该 Artifact 未声明 producing Owner，系统不会猜测路由。");
      }
      if (selection && selection.artifactId !== artifact.id) {
        throw new Error("当前选择不属于此 Artifact，已拒绝发送。");
      }

      const scientificUiSelection =
        selection?.renderer === "scientific-ui"
          ? scientificUiArtifactInquirySelection(selection, revision)
          : undefined;
      if (
        selection?.renderer === "scientific-ui" &&
        (!scientificUiSelection || revision === undefined)
      ) {
        throw new Error("当前图表选择不完整或已过期；请重新选择数据点后再询问。");
      }
      const inquirySelection = scientificUiSelection ?? selection;
      const selectionRenderer = inquirySelection?.renderer;
      const reportSelection = isReportScientificSelection(inquirySelection);
      const structuredScientificSelection =
        selectionRenderer === "curve" || selectionRenderer === "flow";
      const structureScientificSelection = selectionRenderer === "structure";
      const revisionedScientificSelection =
        structuredScientificSelection || Boolean(scientificUiSelection);
      const proposal = intent === "change_proposal";
      if (
        proposal &&
        (!inquirySelection ||
          (!structuredScientificSelection && !reportSelection))
      ) {
        throw new Error(
          "当前仅支持已版本化 Curve/Flow 或报告选中文本的受治理修改。",
        );
      }
      if (proposal && structuredScientificSelection && revision === undefined) {
        throw new Error(
          "当前 Curve/Flow 选择缺少 canonical revision，不能生成受控变更提案。",
        );
      }
      if (reportSelection && !artifact.contentSha256) {
        throw new Error(
          "报告内容尚未获得服务器快照摘要；请等待 Artifact 刷新后重试。",
        );
      }
      if (
        structureScientificSelection &&
        (!artifact.coordinateSha256 ||
          inquirySelection?.renderer !== "structure" ||
          inquirySelection.coordinateSha256 !== artifact.coordinateSha256)
      ) {
        throw new Error(
          "当前结构坐标摘要不可验证；请刷新 Artifact 后再发送残基选择。",
        );
      }

      let currentOrganization = organization;
      if (
        !currentOrganization ||
        organizationError ||
        !currentOrganization.enabled ||
        !currentOrganization.acceptingInput ||
        !currentOrganization.agents.some(
          (agent) => agent.id === ownerAgentId && agent.liveWorker,
        )
      ) {
        currentOrganization = await refreshOrganization(activeProject.projectId);
      }
      if (
        !currentOrganization?.enabled ||
        !currentOrganization.acceptingInput ||
        !currentOrganization.agents.some(
          (agent) => agent.id === ownerAgentId && agent.liveWorker,
        )
      ) {
        throw new Error(
          `Artifact Owner ${ownerAgentId} 当前不在可接收输入的 live Organization 中。`,
        );
      }

      const requestId = `artifact-inquiry-${Date.now()}-${Math.random()
        .toString(16)
        .slice(2)}`;
      const artifactContext = {
        version: 1 as const,
        artifact_id: artifact.id,
        ...(revisionedScientificSelection && revision !== undefined
          ? { artifact_revision: revision }
          : {}),
        ...(inquirySelection
          ? {
              selection: inquirySelection as unknown as Record<string, unknown>,
            }
          : {}),
        ...(reportSelection && artifact.contentSha256
          ? { content_sha256: artifact.contentSha256 }
          : {}),
        ...(structureScientificSelection && artifact.coordinateSha256
          ? { coordinate_sha256: artifact.coordinateSha256 }
          : {}),
        intent: proposal ? ("propose_change" as const) : ("inquiry" as const),
      };
      const optimistic: ProjectTurn = {
        id: `optimistic-${requestId}`,
        requestId,
        projectId: activeProject.projectId,
        kind: "organization_message",
        input: requestText,
        status: "pending",
        createdAt: Date.now(),
        attachments: [],
        artifactContext: {
          version: 1,
          artifact_id: artifact.id,
          owner_agent_id: ownerAgentId,
          ...(revisionedScientificSelection && revision !== undefined
            ? { artifact_revision: revision }
            : {}),
          ...(inquirySelection ? { selection: inquirySelection } : {}),
          ...(reportSelection && artifact.contentSha256
            ? { content_sha256: artifact.contentSha256 }
            : {}),
          ...(structureScientificSelection && artifact.coordinateSha256
            ? { coordinate_sha256: artifact.coordinateSha256 }
            : {}),
          intent: artifactContext.intent,
        },
        routedAgentIds: [ownerAgentId],
        activityIds: [],
        artifactIds: [],
        evidenceIds: [],
        steeredInputs: [],
        raw: {},
      };
      const projectId = activeProject.projectId;
      const scopeGeneration = scopeGenerationRef.current;
      const requestContent =
        proposal && structuredScientificSelection
          ? scientificOperationRequestForSelection(
              requestText,
              inquirySelection,
              artifact.path,
              ownerAgentId,
            )
          : proposal && reportSelection && artifact.contentSha256
            ? reportTextReplacementRequestForSelection(
                requestText,
                inquirySelection,
                artifact.id,
                artifact.contentSha256,
              )
          : requestText;
      if (proposal) setScientificOperation(undefined);
      setOptimisticTurn(optimistic);
      setTurnsError(undefined);
      setTurnsErrorOrigin(undefined);
      try {
        await api.sendOrganizationMessage(ownerAgentId, {
          project_id: projectId,
          request_id: requestId,
          content: requestContent,
          display_input: requestText,
          need_reply: true,
          artifact_context: artifactContext,
        });
        if (
          scopeGeneration !== scopeGenerationRef.current ||
          projectId !== activeProjectIdRef.current
        ) {
          return;
        }
        setOptimisticTurn((current) =>
          current?.requestId === requestId
            ? { ...current, status: "running" }
            : current,
        );
        await Promise.all([
          refreshProjectTurns(projectId),
          refreshOrganization(projectId),
        ]);
        return requestId;
      } catch (cause) {
        const message = errorMessage(cause, "Artifact inquiry 发送失败，请重试。");
        if (
          scopeGeneration === scopeGenerationRef.current &&
          projectId === activeProjectIdRef.current
        ) {
          setRequestError(message);
          setOptimisticTurn((current) =>
            current?.requestId === requestId
              ? { ...current, status: "error", error: message }
              : current,
          );
        }
        throw cause;
      }
    },
    [
      activeProject,
      demoRequested,
      organization,
      organizationError,
      refreshOrganization,
      refreshProjectTurns,
    ],
  );

  const surfaceError = requestError ?? bootstrapError;

  return (
    <CadenceBioApp
      {...adapter}
      readOnly={demoRequested}
      composerDisabled={demoRequested || !activeProject}
      composerDisabledReason={
        demoRequested
          ? "演示模式仅供查看。切换到 Live 项目后可向产物 Owner 发送请求。"
          : !activeProject
            ? "请先创建或选择 Live 项目，再向产物 Owner 发送请求。"
            : undefined
      }
      activeProjectId={
        demoRequested ? adapter.activeProjectId : activeProjectId
      }
      onProjectSelect={selectProject}
      projectListState={demoRequested ? "ready" : projectListState}
      projectListError={projectListError}
      projectContentState={demoRequested ? "ready" : projectContentState}
      projectSnapshotError={projectSnapshotError}
      onRetryProjects={() => void loadProjects()}
      onRetryProject={() => {
        if (activeProject) void loadProjectContent(activeProject);
      }}
      creatingProject={creatingProject}
      createProjectError={createProjectError}
      projectNotice={projectNotice}
      onProjectNoticeDismiss={() => setProjectNotice(undefined)}
      onCreateProject={demoRequested ? undefined : createProject}
      localFolderImportAvailable={
        !demoRequested && bootstrap?.capabilities?.local_folder_import === true
      }
      onSelectLocalFolder={demoRequested ? undefined : () => api.selectLocalFolder()}
      onImportProject={demoRequested ? undefined : importProject}
      userError={identityError}
      onboardingActive={demoRequested ? false : onboardingActive}
      onboardingStep={onboardingStep}
      onOnboardingStepChange={setOnboardingStep}
      onOnboardingDismiss={dismissOnboarding}
      onOnboardingUnavailable={pauseOnboarding}
      onOnboardingComplete={completeOnboarding}
      onOnboardingReplay={demoRequested ? undefined : replayOnboarding}
      requestError={surfaceError}
      organizationError={demoRequested ? undefined : organizationError}
      onRetryOrganization={() => {
        if (activeProject) void refreshOrganization(activeProject.projectId);
      }}
      turns={
        demoRequested ? undefined : visibleProjectTurns.map(toCadenceProjectTurn)
      }
      turnsLoading={demoRequested ? false : turnsLoading}
      turnsLoadingOlder={demoRequested ? false : turnsLoadingOlder}
      turnsHasMore={demoRequested ? false : turnsHasMore}
      turnsError={demoRequested ? undefined : turnsError}
      onRetryTurns={() => {
        if (!activeProject) return;
        if (turnsErrorOrigin === "older") {
          void loadOlderProjectTurns(turnsErrorCursor);
          return;
        }
        void refreshProjectTurns(activeProject.projectId, true);
      }}
      turnsRetryLabel={
        turnsErrorOrigin === "older" ? "重试加载更早请求" : "重试加载请求历史"
      }
      onLoadOlderTurns={
        demoRequested || !turnsHasMore ? undefined : loadOlderProjectTurns
      }
      evidence={
        demoRequested
          ? undefined
          : (snapshot?.evidence ?? []).map(toCadenceEvidence)
      }
      warnings={demoRequested ? undefined : (snapshot?.warnings ?? [])}
      permissions={
        demoRequested
          ? undefined
          : (snapshot?.permissions ?? []).map(toCadencePermission)
      }
      // Demo ProjectTurns reference the adapter's stable Artifact IDs. Passing
      // its records through keeps the thread, Inspector, and provenance view
      // on one fixture instead of silently resolving links against stale data.
      artifacts={demoRequested ? demo.artifacts : artifacts}
      activeTurnId={
        demoRequested
          ? undefined
          : activeProjectTurnId
      }
      permissionError={demoRequested ? undefined : permissionError}
      projectOwnerDisplayName={
        demoRequested ||
        !activeProject ||
        !identity ||
        activeProject.ownerUserId !== identity.userId
          ? undefined
          : identity.displayName
      }
      scientificOperation={scientificOperation}
      onReportTextReplacementApply={applyReportTextReplacement}
      artifactOpenRequest={artifactOpenRequest}
      onSendPrompt={(input, context, cadenceAttachments, intent, references) =>
        sendPrompt(input, context, cadenceAttachments, intent, references)
      }
      onSteerPrompt={demoRequested ? undefined : steerPrompt}
      onArtifactComposerSubmit={sendArtifactInquiry}
      onPermissionDecision={decidePermission}
      onCancelTurn={cancelTurn}
      onUploadFiles={(files) => {
        void uploadFiles(files);
      }}
      onRetryUpload={
        failedUploadFilesRef.current.length
          ? () => {
              void uploadFiles(failedUploadFilesRef.current);
            }
          : undefined
      }
      onAttachProjectInput={attachProjectInput}
      onRemoveAttachment={removeAttachment}
      onBTWResponseDismiss={(requestId) => {
        setBtwResponse((current) =>
          current?.requestId === requestId ? undefined : current,
        );
      }}
      onSendBTW={async (agentId, input, context) => {
        return handleBTW(agentId, input, context);
      }}
      onArtifactDetailRequest={handleArtifactDetailRequest}
      artifactDetailStates={demoRequested ? undefined : artifactDetailStates}
      artifactDetailErrors={demoRequested ? undefined : artifactDetailErrors}
      onArtifactDetailRetry={demoRequested ? undefined : retryArtifactDetail}
      onScientificArtifactApply={applyScientificArtifact}
      researchStateViews={demoRequested ? undefined : researchStateViews}
      researchStateLoadingSections={
        demoRequested ? undefined : researchStateLoadingSections
      }
      researchStateErrors={demoRequested ? undefined : researchStateErrors}
      onResearchStateOpen={
        demoRequested || !activeProject
          ? undefined
          : () =>
              loadResearchStateSections(
                activeProject.projectId,
                INITIAL_RESEARCH_STATE_SECTIONS,
              )
      }
      onResearchStateSectionLoad={
        demoRequested || !activeProject
          ? undefined
          : (section) =>
              void loadResearchStateSection(activeProject.projectId, section)
      }
      onResearchStateRefresh={
        demoRequested || !activeProject
          ? undefined
          : () => {
              const loaded = [...loadedResearchStateSectionsRef.current];
              loadResearchStateSections(
                activeProject.projectId,
                [
                  ...new Set([
                    ...INITIAL_RESEARCH_STATE_SECTIONS,
                    ...loaded,
                  ]),
                ],
              );
            }
      }
      onResearchStateArtifactOpen={openResearchStateArtifact}
    />
  );
}

function descriptorArtifactIdFromPayload(
  payload: PresentationCompletedPayload,
): string | undefined {
  const value =
    typeof payload.descriptor_artifact_id === "string"
      ? payload.descriptor_artifact_id.trim()
      : undefined;
  if (!value) return undefined;
  return value.startsWith("artifact://")
    ? value.slice("artifact://".length)
    : value;
}

function toCadenceAttachment(attachment: AttachmentRef): CadenceAttachment {
  return {
    id: attachment.id ?? `${attachment.kind}-${attachment.name}`,
    name: attachment.name,
    size: attachment.size,
    mimeType: attachment.mimeType,
    kind: attachment.kind,
    path: attachment.path,
    projectId: attachment.projectId,
    sessionId: attachment.sessionId,
    workspacePath: attachment.workspacePath,
    inspection: attachment.inspection,
    documentSelection: attachment.documentSelection,
  };
}

function toCadenceProjectTurn(turn: ProjectTurn): CadenceTurn {
  return {
    id: turn.id,
    requestId: turn.requestId,
    projectId: turn.projectId,
    kind: turn.kind,
    input: turn.input,
    output: turn.resultSummary ?? "",
    status: turn.status,
    createdAt: turn.createdAt,
    completedAt: turn.completedAt,
    error: turn.error,
    attachments: turn.attachments.map(toCadenceAttachment),
    routedAgentIds: turn.routedAgentIds,
    activityIds: turn.activityIds,
    resultSummary: turn.resultSummary,
    artifactIds: turn.artifactIds,
    evidenceIds: turn.evidenceIds,
    artifactContext: turn.artifactContext,
    intent: turn.intent,
    primaryAgentId: turn.primaryAgentId,
    steeredInputs: turn.steeredInputs,
    optimistic: turn.id.startsWith("optimistic-"),
    submissionUncertain: turn.submissionUncertain,
  };
}

function toCadenceEvidence(evidence: EvidenceRecord): CadenceEvidence {
  return {
    id: evidence.id,
    title: evidence.title,
    status: evidence.status,
    origin: evidence.origin,
    confidence: evidence.confidence,
    confidenceExplanation: evidence.confidenceExplanation,
    summary: evidence.summary,
    warnings: evidence.warnings,
    notes: evidence.notes,
    source: evidence.source,
  };
}

function toCadencePermission(
  request: PermissionRequest,
): CadencePermissionRequest {
  return {
    requestId: request.requestId,
    target: request.target,
    reason: request.reason,
    status: request.status,
  };
}

function errorMessage(cause: unknown, fallback: string): string {
  return cause instanceof Error && cause.message.trim()
    ? cause.message
    : fallback;
}

function resolveActiveProjectTurnId(
  snapshotActiveTurnId: string | undefined,
  turns: ProjectTurn[],
  organizationAgentEvents: OrganizationAgentEventRecord[],
): string | undefined {
  const isActive = (turn: ProjectTurn | undefined) => {
    if (
      !turn ||
      turn.submissionUncertain ||
      (turn.status !== "pending" && turn.status !== "running")
    ) {
      return false;
    }
    return (
      turn.kind !== "organization_root" ||
      organizationAgentEvents.some(
        (event) =>
          event.projectId === turn.projectId &&
          event.event.type === "turn_started" &&
          turn.activityIds.includes(event.submissionId),
      )
    );
  };
  const canonicalId = snapshotActiveTurnId?.trim();
  const canonicalTurn = canonicalId
    ? turns.find(
        (turn) => turn.id === canonicalId || turn.requestId === canonicalId,
      )
    : undefined;
  if (canonicalTurn && isActive(canonicalTurn)) return canonicalTurn.id;
  const latestTurn = turns.at(-1);
  return latestTurn && isActive(latestTurn) ? latestTurn.id : undefined;
}

function cadenceArtifactFromRecord(record: ArtifactRecord): CadenceArtifact {
  const base = toCadenceArtifact(record);
  return {
    ...base,
    kind: record.kind,
    mimeType: record.mimeType,
    description:
      stringFromRaw(record.raw, "description") ??
      stringFromRaw(record.raw, "summary") ??
      base.description,
      updatedAt: artifactTimestampFromRaw(record.raw),
    sourceRevision: revisionFromRaw(record.raw, [
      "source_revision",
      "artifact_revision",
      "revision",
    ]),
    descriptorRevision: revisionFromRaw(record.raw, ["descriptor_revision"]),
    previewUrl: record.previewUrl,
    downloadUrl: record.downloadUrl,
    provenance: cadenceArtifactProvenance(record) ?? base.provenance,
  };
}

function artifactWithAuthoritativeTimestamp(
  artifact: CadenceArtifact,
): CadenceArtifact {
  return { ...artifact, updatedAt: undefined };
}

function artifactTimestampFromRaw(raw: unknown): string | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const record = raw as Record<string, unknown>;
  const value =
    record.modified_ms ??
    record.modified_at ??
    record.modifiedAt ??
    record.updated_at ??
    record.updatedAt;
  const timestamp =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim()
        ? Number.isFinite(Number(value))
          ? Number(value)
          : Date.parse(value.trim())
        : undefined;
  if (timestamp === undefined || !Number.isFinite(timestamp)) return undefined;
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? undefined : date.toLocaleString();
}

function stringFromRaw(raw: unknown, key: string): string | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const value = (raw as Record<string, unknown>)[key];
  return typeof value === "string" && value.trim() ? value : undefined;
}

function cadenceArtifactProvenance(
  record: ArtifactRecord,
): CadenceArtifact["provenance"] | undefined {
  const summary = record.provenanceSummary;
  const summarySource = [summary?.source, summary?.recordType, summary?.tool]
    .filter((value): value is string => Boolean(value))
    .join(" · ");
  if (summarySource) {
    return [{ source: summarySource, evidenceId: record.executionId }];
  }
  const rawProvenance = record.raw.provenance ?? record.provenance;
  const candidates = Array.isArray(rawProvenance)
    ? rawProvenance
    : rawProvenance &&
        typeof rawProvenance === "object" &&
        !Array.isArray(rawProvenance)
      ? [rawProvenance]
      : [];
  const mapped = candidates.flatMap((candidate) => {
    if (
      !candidate ||
      typeof candidate !== "object" ||
      Array.isArray(candidate)
    ) {
      return [];
    }
    const value = candidate as Record<string, unknown>;
    const mcp =
      value.mcp && typeof value.mcp === "object" && !Array.isArray(value.mcp)
        ? (value.mcp as Record<string, unknown>)
        : undefined;
    const source = [
      stringFromRaw(value, "source"),
      stringFromRaw(value, "label"),
      stringFromRaw(value, "agent_id"),
      mcp && typeof mcp.server === "string" && typeof mcp.tool === "string"
        ? `${mcp.server}/${mcp.tool}`
        : undefined,
    ]
      .filter((item): item is string => Boolean(item))
      .join(" · ");
    if (!source) return [];
    return [
      {
        source,
        recordedAt:
          stringFromRaw(value, "recorded_at") ??
          stringFromRaw(value, "recordedAt"),
        evidenceId:
          stringFromRaw(value, "evidence_id") ??
          stringFromRaw(value, "evidenceId"),
      },
    ];
  });
  return mapped.length ? mapped : undefined;
}

function previewKindFromDetail(
  mimeType: string | undefined,
  name: string,
  content: string | undefined,
  kind?: string,
  publishedFormat?: CadencePublishedArtifactFormat,
): NonNullable<CadenceArtifact["preview"]>["kind"] {
  switch (publishedFormat) {
    case "document":
      return "markdown";
    case "table":
      return "table";
    case "figure":
      return "image";
    case "html":
      return "html";
    case "code":
      return "code";
    case "decision":
      return "decision";
  }
  const normalizedMime = normalizeMimeType(mimeType);
  const normalizedName = name.trim().toLowerCase();
  if (structureFormatFromDetail(mimeType, name, kind)) {
    return "structure";
  }
  if (normalizedMime === "text/html" || normalizedName.endsWith(".html"))
    return "html";
  if (
    normalizedMime === "text/csv" ||
    normalizedMime === "application/csv" ||
    normalizedName.endsWith(".csv")
  )
    return "table";
  if (normalizedMime?.startsWith("image/")) return "image";
  if (
    normalizedMime === "text/markdown" ||
    normalizedName.endsWith(".md") ||
    normalizedName.endsWith(".markdown")
  )
    return "markdown";
  return content?.trim().startsWith("<") ? "html" : "text";
}

function previewTypeFromDetail(
  mimeType: string | undefined,
  name: string,
  kind?: string,
  publishedFormat?: CadencePublishedArtifactFormat,
): CadenceArtifact["type"] {
  switch (publishedFormat) {
    case "document":
      return "Report";
    case "table":
      return "CSV";
    case "figure":
      return "Image";
    case "html":
      return "HTML";
    case "code":
    case "decision":
      return "Data";
  }
  const normalizedMime = normalizeMimeType(mimeType);
  const normalizedName = name.trim().toLowerCase();
  if (structureFormatFromDetail(mimeType, name, kind))
    return "Structure";
  if (normalizedMime === "text/html" || normalizedName.endsWith(".html"))
    return "HTML";
  if (
    normalizedMime === "text/csv" ||
    normalizedMime === "application/csv" ||
    normalizedName.endsWith(".csv")
  )
    return "CSV";
  if (normalizedMime?.startsWith("image/")) return "Image";
  if (
    normalizedMime === "application/x-ipynb+json" ||
    normalizedName.endsWith(".ipynb")
  )
    return "Notebook";
  if (
    normalizedMime ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    normalizedName.endsWith(".docx")
  )
    return "Docx";
  if (
    normalizedMime === "text/markdown" ||
    normalizedName.endsWith(".md") ||
    normalizedName.endsWith(".markdown")
  )
    return "Report";
  return "Data";
}

function structureFormatFromDetail(
  mimeType: string | undefined,
  name: string,
  kind?: string,
): NonNullable<CadenceArtifact["preview"]>["format"] {
  const normalizedMime = normalizeMimeType(mimeType);
  const normalizedName = name.trim().toLowerCase().split(/[?#]/u, 1)[0];
  const normalizedKind = kind?.trim().toLowerCase();
  if (
    normalizedMime === "chemical/x-pdb" ||
    normalizedMime === "chemical/pdb" ||
    normalizedMime === "application/x-pdb" ||
    normalizedKind === "pdb" ||
    normalizedName.endsWith(".pdb") ||
    normalizedName.endsWith(".ent")
  ) {
    return "pdb";
  }
  if (
    normalizedMime === "chemical/x-cif" ||
    normalizedMime === "chemical/cif" ||
    normalizedMime === "application/cif" ||
    normalizedMime === "application/x-cif" ||
    normalizedMime === "chemical/x-mmcif" ||
    normalizedMime === "chemical/mmcif" ||
    normalizedMime === "application/mmcif" ||
    normalizedMime === "application/x-mmcif" ||
    normalizedKind === "cif" ||
    normalizedKind === "mmcif" ||
    normalizedName.endsWith(".cif") ||
    normalizedName.endsWith(".mmcif")
  ) {
    return "cif";
  }
  return undefined;
}

function normalizeMimeType(value?: string): string | undefined {
  const normalized = value?.split(";", 1)[0].trim().toLowerCase();
  return normalized || undefined;
}

function revisionFromRaw(
  raw: unknown,
  keys: readonly string[],
): number | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const record = raw as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return undefined;
}

function scientificDocumentFromRaw(
  raw: unknown,
): ScientificArtifactDocument | undefined {
  const document = scientificDocumentFromRawWithRevision(raw, true);
  return document && hasCanonicalScientificRevision(document)
    ? (document as ScientificArtifactDocument)
    : undefined;
}

function localScientificDocumentFromRaw(
  raw: unknown,
): LocalScientificArtifactDocument | undefined {
  const document = scientificDocumentFromRawWithRevision(raw, false);
  if (!document || hasCanonicalScientificRevision(document)) return undefined;
  return document as LocalScientificArtifactDocument;
}

function scientificDocumentFromRawWithRevision(
  raw: unknown,
  requireCanonicalRevision: boolean,
): ScientificArtifactDocument | LocalScientificArtifactDocument | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const document = raw as Record<string, unknown>;
  if (
    !scientificDocumentHasBaseContract(document, requireCanonicalRevision)
  ) {
    return undefined;
  }
  if (
    document.renderer === "curve" &&
    !curveScientificDocumentIsValid(document)
  ) {
    return undefined;
  }
  if (
    document.renderer === "flow" &&
    !flowScientificDocumentIsValid(document)
  ) {
    return undefined;
  }
  if (hasCanonicalScientificRevision(document)) {
    return document as ScientificArtifactDocument;
  }
  // Keep the source snapshot unmodified. Its absent revision is the safety
  // boundary that prevents this local inspection document from entering CAS.
  return document as LocalScientificArtifactDocument;
}

function scientificDocumentHasBaseContract(
  document: Record<string, unknown>,
  requireCanonicalRevision: boolean,
) {
  return (
    document.cadence_artifact_version === 1 &&
    (document.renderer === "curve" || document.renderer === "flow") &&
    (!requireCanonicalRevision || hasCanonicalScientificRevision(document)) &&
    isRecord(document.source) &&
    isRecord(document.data) &&
    isRecord(document.state) &&
    isRecord(document.derived) &&
    validVersions(document.versions) &&
    validProvenance(document.provenance)
  );
}

function hasCanonicalScientificRevision(document: Record<string, unknown>) {
  return (
    Number.isSafeInteger(document.revision) &&
    Number(document.revision) >= 1
  );
}

function curveScientificDocumentIsValid(document: Record<string, unknown>) {
  const source = document.source as Record<string, unknown>;
  const data = document.data as Record<string, unknown>;
  const state = document.state as Record<string, unknown>;
  const derived = document.derived as Record<string, unknown>;
  const concentration = source.concentration;
  const mapping = source.x_mapping;
  const parameters = derived.parameters;
  const confidenceBand = derived.response_confidence_band_95;
  const points = data.points;
  const geometry = derived.curve_geometry;
  if (
    (source.kind !== "csv" && source.kind !== "xlsx") ||
    !nonEmptyString(source.path) ||
    !validSha256(source.sha256) ||
    source.dose_unit !== "nM" ||
    !isRecord(concentration) ||
    concentration.field !== "dose_nm" ||
    concentration.unit !== source.dose_unit ||
    !isRecord(mapping) ||
    mapping.name !== "log10_concentration" ||
    mapping.input_field !== "dose_nm" ||
    mapping.input_unit !== source.dose_unit ||
    mapping.output_unit !== `log10(${source.dose_unit})` ||
    mapping.formula !== "log10(dose_nm)" ||
    state.model !== "4PL" ||
    !Array.isArray(state.visible_replicates) ||
    !state.visible_replicates.every(nonEmptyString) ||
    !Array.isArray(state.excluded_point_ids) ||
    !state.excluded_point_ids.every(nonEmptyString) ||
    !isRecord(parameters) ||
    !["bottom", "top", "ic50_nm", "hill_slope"].every((key) =>
      finiteNumber(parameters[key]),
    ) ||
    !Array.isArray(points) ||
    points.length < 5 ||
    !Array.isArray(geometry) ||
    geometry.length !== 120 ||
    !validCurvePoints(points) ||
    !validCurveGeometry(geometry) ||
    !validCurveConfidenceBand(confidenceBand, geometry)
  ) {
    return false;
  }
  const pointIds = new Set(
    points.map((point) => (point as Record<string, unknown>).id),
  );
  return (
    pointIds.size === points.length &&
    state.excluded_point_ids.every((id) => pointIds.has(id))
  );
}

function validCurvePoints(points: unknown[]) {
  return points.every((point) => {
    if (!isRecord(point)) return false;
    return (
      nonEmptyString(point.id) &&
      finitePositiveNumber(point.dose_nm) &&
      finiteNumber(point.response_percent) &&
      nonEmptyString(point.replicate)
    );
  });
}

function validCurveGeometry(geometry: unknown[]) {
  return geometry.every((point) => {
    if (!isRecord(point)) return false;
    return (
      finitePositiveNumber(point.dose_nm) &&
      finiteNumber(point.response_percent)
    );
  });
}

function validCurveConfidenceBand(value: unknown, geometry: unknown[]) {
  if (
    !isRecord(value) ||
    !Array.isArray(value.samples) ||
    value.samples.length !== geometry.length
  ) {
    return false;
  }
  return (
    value.method === "scipy_curve_fit_covariance_delta_method" &&
    value.confidence_level === 0.95 &&
    Array.isArray(value.parameter_order) &&
    value.parameter_order.join(",") === "bottom,top,ic50_nm,hill_slope" &&
    value.samples.every((sample) => {
      if (!isRecord(sample)) return false;
      return (
        finitePositiveNumber(sample.dose_nm) &&
        finiteNumber(sample.lower_response_percent) &&
        finiteNumber(sample.upper_response_percent) &&
        Number(sample.lower_response_percent) <= Number(sample.upper_response_percent)
      );
    })
  );
}

function flowScientificDocumentIsValid(document: Record<string, unknown>) {
  const source = document.source as Record<string, unknown>;
  const data = document.data as Record<string, unknown>;
  const state = document.state as Record<string, unknown>;
  const derived = document.derived as Record<string, unknown>;
  const channels = data.channels;
  const gates = state.gates;
  if (
    source.kind !== "fcs" ||
    !nonEmptyString(source.path) ||
    !validSha256(source.sha256) ||
    !nonEmptyString(source.sample_id) ||
    !isRecord(source.compensation) ||
    source.compensation.reference !== "FCS" ||
    !nonEmptyString(source.compensation.semantics) ||
    !validFlowTransformSpecForName(source.transform) ||
    !isRecord(source.event_authority) ||
    source.event_authority.kind !== "full_fcs_events" ||
    !nonEmptyString(source.event_authority.semantics) ||
    !Number.isSafeInteger(data.event_count) ||
    Number(data.event_count) < 1 ||
    !Array.isArray(channels) ||
    channels.length < 2 ||
    !validFlowChannels(channels) ||
    !Array.isArray(data.sampled_events) ||
    data.sampled_events.length > Number(data.event_count) ||
    !validFlowEvents(data.sampled_events) ||
    !nonEmptyString(state.x_channel) ||
    !nonEmptyString(state.y_channel) ||
    state.x_channel === state.y_channel ||
    !validFlowTransformSpec(state.transform, state.transform_spec) ||
    !Array.isArray(gates) ||
    gates.length < 1 ||
    !validFlowGates(gates, channels) ||
    derived.population_authority !== "full_fcs_events" ||
    !isRecord(derived.populations) ||
    !validFlowPopulations(derived.populations, gates, channels, Number(data.event_count))
  ) {
    return false;
  }
  const channelIds = new Set(
    channels.map((channel) => (channel as Record<string, unknown>).id),
  );
  return channelIds.has(state.x_channel) && channelIds.has(state.y_channel);
}

function validFlowChannels(channels: unknown[]) {
  const ids = new Set<string>();
  return channels.every((channel) => {
    if (!isRecord(channel) || !nonEmptyString(channel.id) || !nonEmptyString(channel.label)) {
      return false;
    }
    if (ids.has(channel.id)) return false;
    ids.add(channel.id);
    return true;
  });
}

function validFlowEvents(events: unknown[]) {
  const ids = new Set<string>();
  return events.every((event) => {
    if (!isRecord(event) || !nonEmptyString(event.id) || !isRecord(event.values)) {
      return false;
    }
    if (ids.has(event.id)) return false;
    ids.add(event.id);
    return Object.values(event.values).every(finiteNumber);
  });
}

function validFlowGates(gates: unknown[], channels: unknown[]) {
  const ids = new Set<string>();
  const channelIds = new Set(
    channels
      .map((channel) => (channel as Record<string, unknown>).id)
      .filter((id): id is string => typeof id === "string"),
  );
  for (const gate of gates) {
    if (!isRecord(gate) || !nonEmptyString(gate.id) || !nonEmptyString(gate.name)) {
      return false;
    }
    if (ids.has(gate.id) || !nonEmptyString(gate.parent_population)) return false;
    if (gate.parent_population !== "root" && !ids.has(gate.parent_population)) {
      return false;
    }
    if (!validFlowTransform(gate.transform) || !validFlowPolygon(gate.geometry)) {
      return false;
    }
    if (
      !Array.isArray(gate.axes) ||
      gate.axes.length !== 2 ||
      !gate.axes.every(nonEmptyString) ||
      gate.axes[0] === gate.axes[1] ||
      !gate.axes.every((axis) => channelIds.has(axis))
    ) {
      return false;
    }
    ids.add(gate.id);
  }
  return true;
}

function validFlowPolygon(value: unknown) {
  if (!isRecord(value) || value.kind !== "polygon" || !Array.isArray(value.vertices)) {
    return false;
  }
  return (
    value.vertices.length >= 3 &&
    value.vertices.every(
      (vertex) =>
        Array.isArray(vertex) &&
        vertex.length === 2 &&
        vertex.every(finiteNumber),
    )
  );
}

function validFlowTransform(value: unknown) {
  return value === "linear" || value === "asinh-10000-4.5-0";
}

function validFlowTransformSpec(transform: unknown, value: unknown) {
  if (!validFlowTransform(transform) || !isRecord(value)) return false;
  if (transform === "linear") {
    return value.name === "linear" && isRecord(value.parameters) && Object.keys(value.parameters).length === 0;
  }
  return (
    value.name === "asinh" &&
    isRecord(value.parameters) &&
    value.parameters.T === 10000 &&
    value.parameters.M === 4.5 &&
    value.parameters.A === 0
  );
}

function validFlowTransformSpecForName(value: unknown) {
  if (!isRecord(value)) return false;
  if (value.name === "linear") {
    return isRecord(value.parameters) && Object.keys(value.parameters).length === 0;
  }
  return (
    value.name === "asinh" &&
    isRecord(value.parameters) &&
    value.parameters.T === 10000 &&
    value.parameters.M === 4.5 &&
    value.parameters.A === 0
  );
}

function validFlowPopulations(
  populations: Record<string, unknown>,
  gates: unknown[],
  channels: unknown[],
  eventCount: number,
) {
  const channelIds = new Set<string>(
    channels
      .map((channel) => (channel as Record<string, unknown>).id)
      .filter((id): id is string => typeof id === "string"),
  );
  return gates.every((gate) => {
    const gateId = (gate as Record<string, unknown>).id;
    if (typeof gateId !== "string") return false;
    const population = populations[gateId];
    if (!isRecord(population) || !Number.isSafeInteger(population.event_count)) {
      return false;
    }
    if (
      Number(population.event_count) < 0 ||
      Number(population.event_count) > eventCount ||
      !finiteNumber(population.percentage) ||
      !isRecord(population.statistics)
    ) {
      return false;
    }
    return [...channelIds].every((channelId) => {
      const statistics = population.statistics as Record<string, unknown>;
      const stats = statistics[channelId];
      return (
        isRecord(stats) &&
        finiteNumber(stats.median) &&
        finiteNumber(stats.q05) &&
        finiteNumber(stats.q95)
      );
    });
  });
}

function validVersions(value: unknown) {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (version) =>
        isRecord(version) &&
        nonEmptyString(version.id) &&
        nonEmptyString(version.label) &&
        nonEmptyString(version.created_at),
    )
  );
}

function validProvenance(value: unknown) {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (entry) =>
        isRecord(entry) &&
        nonEmptyString(entry.source) &&
        nonEmptyString(entry.recorded_at),
    )
  );
}

function validSha256(value: unknown): value is string {
  return typeof value === "string" && /^[0-9a-f]{64}$/.test(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function finiteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function finitePositiveNumber(value: unknown): value is number {
  return finiteNumber(value) && value > 0;
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

async function resolveRendererDocument(
  raw: unknown,
  descriptorPath: string | undefined,
  listedArtifacts: ArtifactRecord[],
  sessionId: string,
): Promise<ArtifactRendererDocument | undefined> {
  const document = kernelRendererDocumentFromRaw(raw);
  if (!document || document.renderer !== "scientific-ui" || document.catalog) {
    return document;
  }
  const rootArtifact = resolveCatalogArtifact(
    (document as ScientificUiArtifactDocument).root,
    descriptorPath,
    listedArtifacts,
  );
  if (!rootArtifact) return document;
  const detail = await api.getArtifact(sessionId, rootArtifact.id);
  const catalog = scientificUiCatalogFromRaw(detail.raw);
  return catalog ? { ...document, catalog } : document;
}

function resolveCatalogArtifact(
  rootRef: string,
  descriptorPath: string | undefined,
  listedArtifacts: ArtifactRecord[],
): ArtifactRecord | undefined {
  const normalizedRef = rootRef.trim();
  if (!normalizedRef || normalizedRef.startsWith("/")) return undefined;
  const directRef = normalizedRef.startsWith("artifact://")
    ? normalizedRef.slice("artifact://".length)
    : normalizedRef;
  const direct = listedArtifacts.find(
    (artifact) => artifact.id === directRef || artifact.path === directRef,
  );
  if (direct) return direct;
  if (!descriptorPath || normalizedRef.startsWith("artifact://"))
    return undefined;
  const descriptorParts = descriptorPath.split("/").filter(Boolean);
  descriptorParts.pop();
  for (const part of normalizedRef.split("/")) {
    if (!part || part === ".") continue;
    if (part === "..") return undefined;
    descriptorParts.push(part);
  }
  const siblingPath = descriptorParts.join("/");
  return listedArtifacts.find((artifact) => artifact.path === siblingPath);
}

function scientificOperationRequest(
  input: string,
  context: CadenceContext,
  routeAgent?: string,
) {
  return scientificOperationRequestForSelection(
    input,
    context.scientificSelection,
    context.artifactPath,
    routeAgent,
  );
}

function scientificOperationRequestForSelection(
  input: string,
  selection: ScientificSelection | undefined,
  artifactPath: string | undefined,
  routeAgent?: string,
) {
  if (!selection) return input;
  if (selection.renderer !== "curve" && selection.renderer !== "flow") {
    return input;
  }
  if (!artifactPath) {
    throw new Error("当前科学产物缺少 artifact_path，无法运行受控科学操作。");
  }
  const operationInput = artifactPathForExecution(
    artifactPath,
    routeAgent,
  );
  const operation =
    selection.renderer === "curve"
      ? "curve.exclude_selected_refit"
      : selection.renderer === "flow"
        ? "flow.adjust_selected_gate"
        : undefined;
  return [
    input,
    "",
    "[Scientific operation request]",
    "skill=medical.local-biostat-protocols",
    `input=${operationInput}`,
    operation ? `parameters.operation=${operation}` : undefined,
    `parameters.selection=${JSON.stringify(selectionForOperation(selection))}`,
  ]
    .filter((line): line is string => Boolean(line))
    .join("\n");
}

function reportTextReplacementRequestForSelection(
  input: string,
  selection: ScientificSelection | undefined,
  artifactId: string,
  contentSha256: string,
) {
  if (!isReportScientificSelection(selection)) return input;
  return [
    input,
    "",
    "[Report text replacement request]",
    "operation=report.replace_selection",
    `artifact_id=${artifactId}`,
    `base_content_sha256=${contentSha256}`,
    `selection=${JSON.stringify(selection)}`,
    "response_contract=return_only_structured_json_proposal",
    "replacement_scope=replace_quote_only",
  ].join("\n");
}

function artifactPathForExecution(path: string, agentId?: string) {
  const prefix = agentId ? `agents/${agentId}/` : undefined;
  return prefix && path.startsWith(prefix) ? path.slice(prefix.length) : path;
}

function cadenceContextLine(
  project: ProjectSummary,
  context: CadenceContext,
  routeAgent?: string,
) {
  return [
    `project=${project.projectId}`,
    `session=${project.sessionId}`,
    `workspace=${project.workspacePath}`,
    routeAgent ? `agent=${routeAgent}` : "agent=cadence-router",
    `tab=${context.tab}`,
    context.artifactId ? `artifact=${context.artifactId}` : undefined,
    context.artifactPath ? `artifact_path=${context.artifactPath}` : undefined,
    context.artifactRevision !== undefined
      ? `artifact_revision=${context.artifactRevision}`
      : undefined,
    context.artifactExecutionId
      ? `execution_id=${context.artifactExecutionId}`
      : undefined,
    context.presentationArtifactRefs.length
      ? `presentation_artifact_refs=${JSON.stringify(context.presentationArtifactRefs)}`
      : undefined,
    context.scientificSelection
      ? `scientific_selection=${JSON.stringify(context.scientificSelection)}`
      : undefined,
    context.stableResidues.length
      ? `stable_residues=${JSON.stringify(context.stableResidues)}`
      : undefined,
    context.residuePositions.length
      ? `residues=${context.residuePositions.join(",")}`
      : undefined,
  ]
    .filter(Boolean)
    .join(" ");
}

function selectionForOperation(selection: ScientificSelection) {
  if (selection.renderer !== "flow" || !selection.gate) {
    return selection;
  }
  return {
    ...selection,
    gate: {
      ...selection.gate,
      geometry: {
        ...selection.gate.geometry,
        vertices: selection.gate.geometry.vertices.map((vertex) => [...vertex]),
      },
    },
  };
}

function isReportScientificSelection(selection?: ScientificSelection) {
  return (
    (selection as unknown as { renderer?: unknown } | undefined)?.renderer ===
    "report"
  );
}

function scientificOperationFromUnknown(
  value: unknown,
  depth = 0,
): ScientificOperationResult | undefined {
  if (depth > 10 || value === null || value === undefined) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return undefined;
    try {
      return scientificOperationFromUnknown(JSON.parse(trimmed), depth + 1);
    } catch {
      return undefined;
    }
  }
  if (typeof value !== "object") return undefined;
  if (Array.isArray(value)) {
    for (const item of value) {
      const operation = scientificOperationFromUnknown(item, depth + 1);
      if (operation) return operation;
    }
    return undefined;
  }
  const record = value as Record<string, unknown>;
  const metrics =
    record.metrics &&
    typeof record.metrics === "object" &&
    !Array.isArray(record.metrics)
      ? (record.metrics as Record<string, unknown>)
      : undefined;
  const direct = metrics?.scientific_operation ?? record.scientific_operation;
  if (isScientificOperation(direct)) return direct;
  for (const key of [
    "tool_result",
    "result",
    "output",
    "response",
    "data",
    "event",
    "raw",
    "turn",
    "turns",
    "messages",
    "items",
    "item",
    "payload",
    "body",
    "structuredContent",
    "Ok",
  ]) {
    const operation = scientificOperationFromUnknown(record[key], depth + 1);
    if (operation) return operation;
  }
  return undefined;
}

function organizationAgentEventType(
  value: unknown,
  depth = 0,
): string | undefined {
  if (depth > 4 || !value || typeof value !== "object" || Array.isArray(value))
    return undefined;
  const record = value as Record<string, unknown>;
  for (const key of ["event", "payload"]) {
    const nested = record[key];
    if (!nested || typeof nested !== "object" || Array.isArray(nested))
      continue;
    const nestedRecord = nested as Record<string, unknown>;
    if (
      typeof nestedRecord.type === "string" &&
      nestedRecord.type !== "agent.event" &&
      nestedRecord.type !== "organization.agent_event"
    ) {
      return nestedRecord.type.toLowerCase();
    }
    const nestedType = organizationAgentEventType(nestedRecord, depth + 1);
    if (nestedType) return nestedType;
  }
  return typeof record.type === "string" &&
    record.type !== "agent.event" &&
    record.type !== "organization.agent_event"
    ? record.type.toLowerCase()
    : undefined;
}

function shouldRefreshOrganizationForAgentEvent(type?: string) {
  return Boolean(
    type &&
    [
      "agent_message",
      "error",
      "stream_error",
      "task_complete",
      "task_started",
      "turn_aborted",
      "turn_complete",
      "turn_started",
    ].includes(type),
  );
}

function shouldRefreshSnapshotForAgentEvent(type?: string) {
  return Boolean(
    type &&
    [
      "error",
      "stream_error",
      "task_complete",
      "task_started",
      "turn_aborted",
      "turn_complete",
      "turn_started",
    ].includes(type),
  );
}

function shouldRefreshProjectTurnsForEvent(type?: string) {
  if (!type) return false;
  return (
    type.includes("turn") ||
    type.includes("request") ||
    type === "session.snapshot" ||
    [
      "error",
      "stream_error",
      "task_complete",
      "task_completed",
      "task_failed",
      "task_started",
    ].includes(type)
  );
}

function shouldRefreshResearchStateForEvent(type?: string) {
  if (!type) return false;
  return (
    type.includes("artifact") ||
    type.includes("complete") ||
    type.includes("failed") ||
    type === "session.snapshot"
  );
}

function isInternalResearchStateArtifact(
  artifact: ArtifactRecord | CadenceArtifact,
) {
  const name = artifact.name.trim().toLowerCase();
  const path = artifact.path?.trim().toLowerCase();
  return (
    name === "research-state.json" ||
    path?.split("/").filter(Boolean).at(-1) === "research-state.json"
  );
}

function dedupeProjectTurns(turns: ProjectTurn[]) {
  const seenIds = new Set<string>();
  const seenRequestIds = new Set<string>();
  return turns
    .slice()
    .reverse()
    .filter((turn) => {
      if (
        seenIds.has(turn.id) ||
        seenRequestIds.has(turn.requestId)
      )
        return false;
      seenIds.add(turn.id);
      seenRequestIds.add(turn.requestId);
      return true;
    })
    .reverse();
}

function mergeRefreshedProjectTurns(
  current: ProjectTurn[],
  refreshed: ProjectTurn[],
) {
  const refreshedIds = new Set(refreshed.map((turn) => turn.id));
  const refreshedRequestIds = new Set(refreshed.map((turn) => turn.requestId));
  return dedupeProjectTurns([
    ...current.filter(
      (turn) =>
        !refreshedIds.has(turn.id) &&
        !refreshedRequestIds.has(turn.requestId),
    ),
    ...refreshed,
  ]);
}

function mergeAttachments(
  current: AttachmentRef[],
  incoming: AttachmentRef[],
) {
  const key = (attachment: AttachmentRef) =>
    attachment.id ?? attachment.path ?? `${attachment.kind}:${attachment.name}`;
  return incoming.reduce((merged, attachment) => {
    const attachmentKey = key(attachment);
    const index = merged.findIndex((candidate) => key(candidate) === attachmentKey);
    return index < 0
      ? [...merged, attachment]
      : merged.map((candidate, candidateIndex) =>
          candidateIndex === index ? attachment : candidate,
        );
  }, current);
}

function refreshSelectedAttachments(
  selected: AttachmentRef[],
  available: AttachmentRef[],
) {
  const key = (attachment: AttachmentRef) =>
    attachment.id ?? attachment.path ?? `${attachment.kind}:${attachment.name}`;
  const availableByKey = new Map(available.map((attachment) => [key(attachment), attachment]));
  return selected.map((attachment) => availableByKey.get(key(attachment)) ?? attachment);
}

function composerIntentRequest(
  intent: CadenceComposerIntent,
  input: string,
): ComposerIntentRequest {
  switch (intent.kind) {
    case "project_investigation":
      return { kind: "project_investigation" };
    case "research_plan":
      return { kind: "research_plan" };
    case "continue_research":
      return {
        kind: "continue_research",
        parent_request_id: intent.parentRequestId,
      };
    case "controlled_command": {
      const command = controlledCommandInput(input)?.command.trim() ?? "";
      // Open Science's `!` is local shell input. Cadence preserves the familiar
      // affordance but sends only a managed Runtime/Sandbox request for review
      // and permission; it must never become an ordinary browser-side command.
      if (!command) {
        throw new Error("请输入要由受控 Runtime/Sandbox 执行的命令。");
      }
      return { kind: "controlled_command", command };
    }
  }
}

function publicProjectTurnIntent(
  intent: CadenceComposerIntent,
): ProjectTurn["intent"] {
  switch (intent.kind) {
    case "project_investigation":
      return { kind: "project_investigation" };
    case "research_plan":
      return { kind: "research_plan" };
    case "controlled_command":
      return { kind: "controlled_command" };
    case "continue_research":
      return {
        kind: "continue_research",
        parentRequestId: intent.parentRequestId,
        targetAgentId: intent.targetAgentId,
      };
  }
}

function composerReferenceRequests(
  references: CadenceComposerReference[],
): ComposerReferenceRequest[] {
  return references.map((reference) =>
    reference.kind === "artifact"
      ? { kind: "artifact", artifact_id: reference.artifactId }
      : { kind: "project_turn", request_id: reference.requestId },
  );
}

function timestampMs(value: string | number | undefined) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return undefined;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function isScientificOperation(
  value: unknown,
): value is ScientificOperationResult {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const operation = value as Record<string, unknown>;
  const selection = operation.selection;
  return (
    operation.version === 1 &&
    (operation.operation === "curve.exclude_selected_refit" ||
      operation.operation === "flow.adjust_selected_gate") &&
    (operation.renderer === "curve" || operation.renderer === "flow") &&
    typeof operation.base_revision === "number" &&
    Boolean(
      selection && typeof selection === "object" && !Array.isArray(selection),
    ) &&
    Boolean(operation.before && typeof operation.before === "object") &&
    Boolean(operation.after && typeof operation.after === "object")
  );
}

function recordHasObject(record: Record<string, unknown>, key: string) {
  const value = record[key];
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function artifactVersionsFromScientific(
  document: ScientificArtifactInspectionDocument,
) {
  return document.versions.map((version) => ({
    id: version.id,
    label: version.label,
    createdAt: version.created_at,
    note: version.note,
  }));
}

function artifactProvenanceFromScientific(
  document: ScientificArtifactInspectionDocument,
) {
  return document.provenance.map((source) => ({
    source: source.source,
    recordedAt: source.recorded_at,
    evidenceId: source.evidence_id,
  }));
}

function organizationStatus(
  status: string | undefined,
  statusError: string | undefined,
): "working" | "waiting" | "paused" | "complete" | "error" | "unknown" {
  // A failed observation does not prove that an Agent failed. Reserve the
  // error state for Runtime facts and show this card as unknown instead.
  if (statusError) return "unknown";
  const value = status?.toLowerCase() ?? "";
  if (!value) return "unknown";
  if (value.includes("error") || value.includes("fail")) return "error";
  if (
    value.includes("complete") ||
    value.includes("done") ||
    value.includes("shutdown")
  ) {
    return "complete";
  }
  if (value.includes("pause")) return "paused";
  if (
    value.includes("run") ||
    value.includes("work") ||
    value.includes("active")
  ) {
    return "working";
  }
  if (value.includes("wait") || value.includes("idle")) return "waiting";
  return "unknown";
}

function organizationAgentAccent(index: number): string {
  return [
    "var(--cadence-brand)",
    "var(--cadence-data-blue)",
    "var(--cadence-data-green)",
    "var(--cadence-data-ochre)",
    "var(--cadence-data-rose)",
    "var(--cadence-data-violet)",
  ][index % 6];
}
